import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { API } from "../lib/api";
import AdminLogin from "./AdminLogin";
import { PaymentQRPanel, PaymentVerificationPanel } from "./AdminPaymentPanels";
import { DocNumberSettingsPanel } from "./AdminDocNumberSettings";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, Trash2, Download, FileText, Pencil, LogOut, Upload, CheckCircle2, XCircle, Truck, QrCode, Eye, Camera } from "lucide-react";
import SortToggle, { useSortDir, applySort } from "../components/SortToggle";
import AdminEmployees from "./AdminEmployees";
import AdminBroadcasts from "./AdminBroadcasts";
import { toast } from "sonner";
import ExportButton from "../components/ExportButton";

const emptyProduct = {
  size: "", our_code: "", packing: 4, tile_area_sqft: 4, tile_type: "floor",
  product_tier: "standard",
  ultra_premium: false,
  surface: "", colour: "", application_area: "",
  primary_uom: "BOX", secondary_uom: "SQ.FT", tertiary_uom: "", tertiary_uom_value: 0,
  gst_pct: 18,
  transit_min_days: 10, transit_max_days: 21,
  mrp: 0, offer_price: 0, rating: 4.5, scode: "", adc: "", image_url: "", description: "", keywords: [], videos: [], images: [], suggested_codes: [], moq: 1,
};

const emptyLayer = {
  name: "", contact: "", address: "", experience_years: 5, photo_url: "", bio: "", rating: 4.5, work_photos: [],
};

const PRODUCT_FIELDS = ["our_code","size","tile_type","product_tier","ultra_premium","surface","colour","application_area","primary_uom","secondary_uom","tertiary_uom","tertiary_uom_value","gst_pct","transit_range","is_returnable","moq","packing","tile_area_sqft","mrp","offer_price","rating","scode","adc"];
const LAYER_FIELDS = ["name","contact","address","experience_years","rating","photo_url"];
const TILE_TYPES = ["floor", "wall"];
const UOM_OPTIONS = ["BOX", "NUMBERS", "BAGS", "SQ.FT", "KG", "GRAM", "DOZEN", "KIT", "LITRE", "MILLILITRE", "PACKET"];

const _rowKey = () => `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const toAbs = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

function ProductDialog({ open, onOpenChange, initial, onSave, mode, tileTypes, onCreateTileType, productTiers, onCreateProductTier }) {
  const [form, setForm] = useState(initial || emptyProduct);
  const [addingType, setAddingType] = useState(false);
  const [newType, setNewType] = useState("");
  const [addingTier, setAddingTier] = useState(false);
  const [newTier, setNewTier] = useState("");
  const [numberingMaster, setNumberingMaster] = useState(null);
  const [numberingLoading, setNumberingLoading] = useState(false);
  useEffect(() => { setForm(initial || emptyProduct); }, [initial]);

  // Watch size → look up numbering master; auto-fill our_code preview when creating
  useEffect(() => {
    if (mode !== "create") return;
    if (!form.size || !form.size.trim()) { setNumberingMaster(null); return; }
    setNumberingLoading(true);
    api.get("/admin/numbering-masters", { params: {} })
      .then(r => {
        const m = (r.data || []).find(x => x.size.toLowerCase() === form.size.trim().toLowerCase());
        setNumberingMaster(m || null);
      })
      .catch(() => setNumberingMaster(null))
      .finally(() => setNumberingLoading(false));
  }, [form.size, mode]);

  // Live preview next code
  useEffect(() => {
    if (mode !== "create" || !numberingMaster) return;
    const sub = form.sub_category || "";
    api.get("/admin/numbering-masters/preview", { params: { size: form.size, sub_category: sub } })
      .then(r => {
        if (r.data?.exists && r.data.next_code) {
          setForm(f => ({ ...f, our_code: r.data.next_code }));
        }
      })
      .catch(() => {});
  }, [numberingMaster, form.sub_category]);

  const submit = () => {
    const rawKeywords = form.keywords;
    const kwList = Array.isArray(rawKeywords)
      ? rawKeywords
      : String(rawKeywords || "").split(",");
    const keywords = kwList.map(k => (k || "").trim()).filter(Boolean);
    const payload = {
      ...form,
      name: form.our_code || form.name,
      transit_min_days: (() => { const mn = parseInt(form.transit_min_days) || 10; const mx = parseInt(form.transit_max_days) || 21; return Math.max(1, Math.min(mn, mx)); })(),
      transit_max_days: (() => { const mn = parseInt(form.transit_min_days) || 10; const mx = parseInt(form.transit_max_days) || 21; return Math.max(1, Math.max(mn, mx)); })(),
      moq: Math.max(1, parseInt(form.moq) || 1),
      tile_area_sqft: +form.tile_area_sqft,
      mrp: +form.mrp,
      offer_price: +form.offer_price,
      rating: +form.rating,
      scode: form.scode || null,
      adc: form.adc || null,
      images: (form.images || []).filter(u => u && u.trim()),
      keywords,
      sub_category: form.sub_category || "",
    };
    // On create: if a numbering master exists, let the server atomically allocate the code (send blank).
    if (mode === "create" && numberingMaster) {
      payload.our_code = "";
    }
    onSave(payload);
  };

  const createNewType = async () => {
    if (!newType.trim()) return;
    const created = await onCreateTileType(newType.trim());
    if (created) {
      setForm({ ...form, tile_type: created.name });
      setNewType("");
      setAddingType(false);
    }
  };

  const createNewTier = async () => {
    if (!newTier.trim()) return;
    const created = await onCreateProductTier(newTier.trim());
    if (created) {
      setForm({ ...form, product_tier: created.name });
      setNewTier("");
      setAddingTier(false);
    }
  };

  const [uploading, setUploading] = useState(false);
  const uploadFiles = async (files, kind) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(true);
    try {
      const currentImages = form.images || [];
      const currentVideos = form.videos || [];
      const newImages = [...currentImages];
      const newVideos = [...currentVideos];
      let primary = form.image_url || "";
      for (const file of list) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads/product-media", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (data.kind === "video") {
          newVideos.push(data.url);
        } else {
          if (!primary) primary = data.url;
          newImages.push(data.url);
        }
      }
      setForm(prev => ({ ...prev, image_url: primary || prev.image_url, images: newImages, videos: newVideos }));
      toast.success(`Uploaded ${list.length} file(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };
  const removeImageAt = (i) => setForm(prev => {
    const imgs = (prev.images || []).filter((_, idx) => idx !== i);
    return { ...prev, images: imgs, image_url: (prev.image_url && (prev.images || [])[i] === prev.image_url) ? (imgs[0] || "") : prev.image_url };
  });
  const removeVideoAt = (i) => setForm(prev => ({ ...prev, videos: (prev.videos || []).filter((_, idx) => idx !== i) }));

  const addImage = () => setForm({ ...form, images: [...(form.images || []), ""] });
  const updateImage = (i, v) => {
    const imgs = [...(form.images || [])];
    imgs[i] = v;
    setForm({ ...form, images: imgs });
  };
  const removeImage = (i) => setForm({ ...form, images: (form.images || []).filter((_, idx) => idx !== i) });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-none">
        <DialogHeader>
          <DialogTitle className="font-editorial text-2xl">
            {mode === "edit" ? "Edit Product" : "New Product"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 mt-4 max-h-[60vh] overflow-y-auto pr-2">
          {PRODUCT_FIELDS.map(f => (
            <div key={f}>
              <Label className="text-xs uppercase tracking-wider text-[#5C5C5C]">{f.replace(/_/g," ")}</Label>
              {f === "tile_type" ? (
                <div>
                  {!addingType ? (
                    <div className="flex gap-2">
                      <select
                        className="flex-1 h-10 border border-input rounded-none px-3 text-sm"
                        value={form[f] ?? "floor"}
                        onChange={(e) => setForm({ ...form, [f]: e.target.value })}
                        data-testid={`product-${f}`}
                      >
                        {(tileTypes || []).map(t => <option key={t.id} value={t.name}>{t.name.toUpperCase()}</option>)}
                      </select>
                      <button type="button" onClick={() => setAddingType(true)} className="text-xs bg-[#C9A968] px-2 hover:bg-[#B89550]" data-testid="add-tile-type-inline">+ New</button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input className="rounded-none flex-1" placeholder="e.g. mosaic" value={newType} onChange={(e) => setNewType(e.target.value)} data-testid="new-tile-type-input" />
                      <button type="button" onClick={createNewType} className="text-xs bg-[#1E2C5A] text-white px-3" data-testid="save-new-tile-type">Save</button>
                      <button type="button" onClick={() => { setAddingType(false); setNewType(""); }} className="text-xs text-[#5C5C5C]">Cancel</button>
                    </div>
                  )}
                </div>
              ) : f === "product_tier" ? (
                <div>
                  {!addingTier ? (
                    <div className="flex gap-2">
                      <select
                        className="flex-1 h-10 border border-input rounded-none px-3 text-sm"
                        value={form[f] ?? "standard"}
                        onChange={(e) => setForm({ ...form, [f]: e.target.value })}
                        data-testid={`product-${f}`}
                      >
                        {(productTiers || []).map(t => <option key={t.id} value={t.name}>{t.name.toUpperCase()}</option>)}
                      </select>
                      <button type="button" onClick={() => setAddingTier(true)} className="text-xs bg-[#C9A968] px-2 hover:bg-[#B89550]" data-testid="add-product-tier-inline">+ New</button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input className="rounded-none flex-1" placeholder="e.g. luxury" value={newTier} onChange={(e) => setNewTier(e.target.value)} data-testid="new-product-tier-input" />
                      <button type="button" onClick={createNewTier} className="text-xs bg-[#1E2C5A] text-white px-3" data-testid="save-new-product-tier">Save</button>
                      <button type="button" onClick={() => { setAddingTier(false); setNewTier(""); }} className="text-xs text-[#5C5C5C]">Cancel</button>
                    </div>
                  )}
                </div>
              ) : f === "is_returnable" ? (
                <select
                  className="w-full h-10 border border-input rounded-none px-3 text-sm"
                  value={form[f] === false ? "false" : "true"}
                  onChange={(e) => setForm({ ...form, [f]: e.target.value === "true" })}
                  data-testid={`product-${f}`}
                >
                  <option value="true">RETURNABLE</option>
                  <option value="false">NOT RETURNABLE</option>
                </select>
              ) : f === "ultra_premium" ? (
                <label className="flex items-center gap-2 h-10 text-sm cursor-pointer" data-testid={`product-${f}-wrap`}>
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-[#C9A968]"
                    checked={!!form[f]}
                    onChange={(e) => setForm({ ...form, [f]: e.target.checked })}
                    data-testid={`product-${f}`}
                  />
                  <span className={form[f] ? "text-[#C9A968] font-medium uppercase tracking-[0.15em] text-xs" : "text-[#8A8A8A] text-xs"}>
                    {form[f] ? "TileRoom Assured" : "Standard"}
                  </span>
                </label>
              ) : f === "transit_range" ? (
                <div className="flex items-center gap-2" data-testid={`product-${f}-wrap`}>
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    className="rounded-none w-20"
                    value={form.transit_min_days ?? 10}
                    onChange={(e) => setForm({ ...form, transit_min_days: Math.max(1, parseInt(e.target.value) || 1) })}
                    data-testid="product-transit-min"
                  />
                  <span className="text-xs text-[#5C5C5C]">to</span>
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    className="rounded-none w-20"
                    value={form.transit_max_days ?? 21}
                    onChange={(e) => setForm({ ...form, transit_max_days: Math.max(1, parseInt(e.target.value) || 1) })}
                    data-testid="product-transit-max"
                  />
                  <span className="text-xs uppercase tracking-[0.15em] text-[#5C5C5C]">business days</span>
                </div>
              ) : (f === "primary_uom" || f === "secondary_uom" || f === "tertiary_uom") ? (
                <select
                  className="w-full h-10 border border-input rounded-none px-3 text-sm"
                  value={form[f] ?? (f === "primary_uom" ? "BOX" : f === "secondary_uom" ? "SQ.FT" : "")}
                  onChange={(e) => setForm({ ...form, [f]: e.target.value })}
                  data-testid={`product-${f}`}
                >
                  {f === "tertiary_uom" && <option value="">— None —</option>}
                  {UOM_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              ) : (
                <Input
                  className="rounded-none"
                  value={form[f] ?? ""}
                  onChange={(e) => setForm({ ...form, [f]: e.target.value })}
                  data-testid={`product-${f}`}
                />
              )}
            </div>
          ))}
          {/* Pricing preview: pre-GST + tertiary calculations */}
          {mode === "create" || mode === "edit" ? (
            <div className="col-span-2 bg-[#F0EFEA] border-l-2 border-[#C9A968] p-3 text-xs space-y-1" data-testid="product-pricing-preview">
              {(() => {
                const price = Number(form.offer_price) || 0;
                const gst = Number(form.gst_pct) || 18;
                const pre = price / (1 + gst / 100);
                const tVal = Number(form.tertiary_uom_value) || 0;
                const tertiary_price = tVal > 0 ? (price / tVal) : null;
                return (
                  <>
                    <p><strong>Selling price (GST-incl):</strong> ₹{price.toFixed(2)} / {form.primary_uom || "BOX"}</p>
                    <p><strong>Pre-GST value:</strong> ₹{pre.toFixed(2)} · GST {gst}% = ₹{(price - pre).toFixed(2)}</p>
                    {tertiary_price !== null && form.tertiary_uom ? (
                      <p><strong>Tertiary display:</strong> ₹{tertiary_price.toFixed(2)} / {form.tertiary_uom} <span className="text-[#8A8A8A]">(auto-calc from ÷ {tVal})</span></p>
                    ) : null}
                  </>
                );
              })()}
            </div>
          ) : null}
          {mode === "create" && form.size && form.size.trim() && (
            <div className="col-span-2 -mt-2" data-testid="numbering-master-hint">
              {numberingLoading ? (
                <p className="text-xs text-[#8A8A8A]">Checking numbering master…</p>
              ) : !numberingMaster ? (
                <div className="bg-[#FDF9EE] border border-[#C9A968]/40 p-3 text-xs text-[#8B7A3E]">
                  ⚠ No prefix master defined for size <strong>{form.size}</strong> — please create one first under the <em>Numbering</em> tab, or enter OUR CODE manually.
                </div>
              ) : (
                <div className="bg-[#F0EFEA] border-l-2 border-[#1E2C5A] p-3 space-y-2">
                  <p className="text-xs">
                    Auto-numbering active for <strong>{numberingMaster.size}</strong> · prefix
                    <span className="font-mono ml-1 px-1 bg-[#1E2C5A] text-white">{numberingMaster.prefix}</span>
                  </p>
                  {(numberingMaster.sub_categories || []).length > 0 && (
                    <div className="flex items-center gap-3">
                      <Label className="text-xs uppercase tracking-wider text-[#5C5C5C]">Sub-category</Label>
                      <select
                        className="h-8 border border-input rounded-none px-2 text-xs bg-white"
                        value={form.sub_category || ""}
                        onChange={(e) => setForm({ ...form, sub_category: e.target.value })}
                        data-testid="product-sub-category"
                      >
                        <option value="">— Default sequence —</option>
                        {numberingMaster.sub_categories.map(s => (
                          <option key={s.id} value={s.name}>{s.name} (next: {numberingMaster.prefix}{s.current_number})</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <p className="text-xs text-[#5C5C5C]">Next allocated code: <span className="font-mono font-medium text-[#1E2C5A]" data-testid="product-next-code">{form.our_code || "—"}</span></p>
                </div>
              )}
            </div>
          )}
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-wider text-[#5C5C5C]">Description</Label>
            <Input className="rounded-none" value={form.description ?? ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="product-description" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-wider text-[#5C5C5C]">Search keywords <span className="text-[#8A8A8A] normal-case tracking-normal">— comma-separated, boosts discoverability</span></Label>
            <Input
              className="rounded-none"
              placeholder="e.g. marble finish, italian look, glossy, ivory, bathroom, kitchen wall"
              value={Array.isArray(form.keywords) ? form.keywords.join(", ") : (form.keywords ?? "")}
              onChange={(e) => setForm({ ...form, keywords: e.target.value })}
              onBlur={(e) => {
                const arr = e.target.value.split(",").map(k => k.trim()).filter(Boolean);
                setForm(prev => ({ ...prev, keywords: arr }));
              }}
              data-testid="product-keywords"
            />
            {Array.isArray(form.keywords) && form.keywords.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="product-keywords-chips">
                {form.keywords.map((k, i) => (
                  <span key={`${k}-${i}`} className="inline-flex items-center gap-1 bg-[#F0EFEA] border border-[#1E2C5A]/20 text-[11px] px-2 py-0.5 text-[#1E2C5A]">
                    {k}
                    <button
                      type="button"
                      onClick={() => setForm(prev => ({ ...prev, keywords: (prev.keywords || []).filter((_, idx) => idx !== i) }))}
                      className="text-[#8A8A8A] hover:text-red-700"
                      data-testid={`kw-remove-${i}`}
                    >×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="col-span-2 border-t pt-4" data-testid="product-media-section">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <Label className="text-xs uppercase tracking-wider text-[#5C5C5C]">Product Media (images + videos)</Label>
              <label className={`inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.15em] cursor-pointer transition-colors ${uploading ? "bg-[#8A8A8A] text-white" : "bg-[#1E2C5A] text-white hover:bg-[#141F45]"}`} data-testid="product-media-upload-label">
                <Upload className="w-3.5 h-3.5" strokeWidth={1.5} />
                {uploading ? "Uploading..." : "Upload Files"}
                <input
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  className="hidden"
                  disabled={uploading}
                  onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }}
                  data-testid="product-media-upload-input"
                />
              </label>
            </div>
            <p className="text-xs text-[#8A8A8A] mb-3">Images ≤5MB · Videos ≤30MB · First image becomes the primary cover. Click any image on the product page to magnify.</p>

            {(form.images || []).length > 0 && (
              <div className="mb-4">
                <p className="label-eyebrow mb-2">Images ({(form.images || []).length})</p>
                <div className="grid grid-cols-4 gap-2">
                  {(form.images || []).map((u, i) => (
                    <div key={u || `img-${i}`} className="relative group" data-testid={`product-image-${i}`}>
                      <img src={u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u} alt={`img-${i}`} className={`w-full aspect-square object-cover ${u === form.image_url ? "ring-2 ring-[#C9A968]" : ""}`} />
                      {u === form.image_url && <span className="absolute bottom-1 left-1 bg-[#C9A968] text-black px-1 text-[10px] uppercase tracking-wider">Cover</span>}
                      <button
                        type="button"
                        onClick={() => setForm(prev => ({ ...prev, image_url: u }))}
                        className="absolute top-1 left-1 bg-white/90 text-[#1E2C5A] px-1 text-[10px] hidden group-hover:block"
                        data-testid={`set-cover-${i}`}
                      >SET COVER</button>
                      <button
                        type="button"
                        onClick={() => removeImageAt(i)}
                        className="absolute top-1 right-1 bg-red-700 text-white w-5 h-5 text-xs flex items-center justify-center"
                        data-testid={`remove-product-image-${i}`}
                      >×</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(form.videos || []).length > 0 && (
              <div>
                <p className="label-eyebrow mb-2">Videos ({(form.videos || []).length})</p>
                <div className="grid grid-cols-3 gap-2">
                  {(form.videos || []).map((u, i) => (
                    <div key={u || `vid-${i}`} className="relative" data-testid={`product-video-${i}`}>
                      <video src={u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u} controls className="w-full aspect-video bg-black object-cover" />
                      <button
                        type="button"
                        onClick={() => removeVideoAt(i)}
                        className="absolute top-1 right-1 bg-red-700 text-white w-5 h-5 text-xs flex items-center justify-center"
                        data-testid={`remove-product-video-${i}`}
                      >×</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(form.images || []).length === 0 && (form.videos || []).length === 0 && (
              <p className="text-xs text-[#8A8A8A]">No media yet. Click &quot;Upload Files&quot; above to add product images and videos.</p>
            )}
          </div>
          <div className="col-span-2 border-t pt-4" data-testid="product-suggested-section">
            <Label className="text-xs uppercase tracking-wider text-[#5C5C5C]">Suggested Design Codes</Label>
            <p className="text-xs text-[#8A8A8A] mt-1 mb-2">Comma-separated product codes that pair well with this design. Buyers see these under <em>Pairs Well With</em> on the product page.</p>
            <textarea
              rows={2}
              className="w-full border border-input rounded-none px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#1E2C5A]"
              placeholder="e.g. A101, A102, B205"
              value={Array.isArray(form.suggested_codes) ? form.suggested_codes.join(", ") : (form.suggested_codes || "")}
              onChange={(e) => setForm({ ...form, suggested_codes: e.target.value.split(",").map(c => c.trim()).filter(Boolean) })}
              data-testid="product-suggested-codes"
            />
            {Array.isArray(form.suggested_codes) && form.suggested_codes.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2" data-testid="suggested-codes-preview">
                {form.suggested_codes.map((c, i) => (
                  <span key={`${c}-${i}`} className="inline-flex items-center gap-1 bg-[#F0EFEA] text-[#1E2C5A] px-2 py-1 text-xs font-mono border border-[#C9A968]/30">
                    {c}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <Button className="rounded-none bg-[#1E2C5A] hover:bg-[#141F45] mt-6" onClick={submit} data-testid="save-product">
          {mode === "edit" ? "Save Changes" : "Create Product"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

const VENDOR_FIELDS = [
  { name: "company_name", label: "Company Name" },
  { name: "vendor_name", label: "Vendor Name" },
  { name: "contact_no", label: "Contact No." },
  { name: "email", label: "Email" },
  { name: "gst_no", label: "GST No." },
];

function VendorDialog({ open, onOpenChange, initial, onSave, mode }) {
  const [form, setForm] = useState(initial || {
    company_name: "", vendor_name: "", contact_no: "", email: "", gst_no: "", address: "",
  });
  useEffect(() => {
    setForm(initial || { company_name: "", vendor_name: "", contact_no: "", email: "", gst_no: "", address: "" });
  }, [initial]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl rounded-none">
        <DialogHeader><DialogTitle className="font-editorial text-2xl">
          {mode === "edit" ? "Edit Vendor" : "New Vendor"}
        </DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4 mt-4">
          {VENDOR_FIELDS.map(f => (
            <div key={f.name}>
              <Label className="text-xs uppercase tracking-wider text-[#5C5C5C]">{f.label}</Label>
              <Input className="rounded-none" value={form[f.name] ?? ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} data-testid={`vendor-${f.name}`} />
            </div>
          ))}
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-wider text-[#5C5C5C]">Address</Label>
            <Input className="rounded-none" value={form.address ?? ""} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="vendor-address" />
          </div>
        </div>
        <Button className="rounded-none bg-[#1E2C5A] hover:bg-[#141F45] mt-6" onClick={() => onSave(form)} data-testid="save-vendor">
          {mode === "edit" ? "Save Changes" : "Add Vendor"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function UploadDialog({ open, onOpenChange, kind, onUpload }) {
  const [file, setFile] = useState(null);
  useEffect(() => { setFile(null); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-none">
        <DialogHeader><DialogTitle className="font-editorial text-2xl">
          Bulk Upload {kind === "products" ? "Products" : "Vendors"}
        </DialogTitle></DialogHeader>
        <p className="text-xs text-[#5C5C5C] mt-2">
          Upload a CSV file. Rows that match an existing {kind === "products" ? "OUR CODE" : "vendor"} will be updated. Download the template above to see the required columns.
        </p>
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => setFile(e.target.files[0])}
          className="mt-4 block w-full text-sm border border-[#1A1A1A]/20 file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-[#1E2C5A] file:text-white file:uppercase file:text-xs file:tracking-wider"
          data-testid="upload-file"
        />
        <Button
          className="rounded-none bg-[#1E2C5A] hover:bg-[#141F45] mt-6"
          disabled={!file}
          onClick={() => onUpload(kind, file)}
          data-testid="upload-submit"
        >
          Upload CSV
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function ReturnsAdminTab({ onRows }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/returns").then(r => { setRows(r.data); onRows && onRows(r.data); });
  useEffect(() => { load(); }, []);
  const act = async (id, kind) => {
    await api.patch(`/admin/returns/${id}/${kind}`);
    toast.success(`Return ${kind}d`);
    load();
  };
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Order</TableHead><TableHead>Customer</TableHead><TableHead>Reason</TableHead>
          <TableHead>Images</TableHead><TableHead>Date</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length ? rows.map(r => (
          <TableRow key={r.id} data-testid={`admin-return-${r.id}`}>
            <TableCell className="font-medium">{r.order_no}</TableCell>
            <TableCell>{r.customer_name}</TableCell>
            <TableCell className="text-xs max-w-xs truncate">{r.reason}</TableCell>
            <TableCell><div className="flex gap-1">{r.images.slice(0,3).map((img, i) => <img key={img || `return-img-${i}`} src={img} className="w-10 h-10 object-cover" alt=""/>)}</div></TableCell>
            <TableCell className="text-xs">{new Date(r.created_at).toLocaleDateString()}</TableCell>
            <TableCell><span className={r.status === "approved" ? "text-green-700" : r.status === "rejected" ? "text-red-700" : "text-[#C9A968]"}>{r.status}</span></TableCell>
            <TableCell className="space-x-2">
              {r.status === "pending" && (<>
                <button onClick={() => act(r.id, "approve")} className="text-green-700 text-xs" data-testid={`approve-return-${r.id}`}>Approve</button>
                <button onClick={() => act(r.id, "reject")} className="text-red-700 text-xs" data-testid={`reject-return-${r.id}`}>Reject</button>
              </>)}
            </TableCell>
          </TableRow>
        )) : <TableRow><TableCell colSpan={7} className="text-center text-[#8A8A8A] py-6">No return requests.</TableCell></TableRow>}
      </TableBody>
    </Table>
  );
}


function LayerDialog({ open, onOpenChange, initial, onSave, mode }) {  const [form, setForm] = useState(initial || emptyLayer);
  useEffect(() => { setForm(initial || emptyLayer); }, [initial]);

  const submit = () => {
    onSave({
      ...form,
      experience_years: +form.experience_years,
      rating: +form.rating,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none">
        <DialogHeader>
          <DialogTitle className="font-editorial text-2xl">
            {mode === "edit" ? "Edit Tile Layer" : "New Tile Layer"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 mt-4">
          {LAYER_FIELDS.map(f => (
            <div key={f}>
              <Label className="text-xs uppercase tracking-wider text-[#5C5C5C]">{f.replace(/_/g," ")}</Label>
              <Input className="rounded-none" value={form[f] ?? ""} onChange={(e) => setForm({ ...form, [f]: e.target.value })} data-testid={`layer-${f}`} />
            </div>
          ))}
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-wider text-[#5C5C5C]">Bio</Label>
            <Input className="rounded-none" value={form.bio ?? ""} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="layer-bio" />
          </div>
        </div>
        <Button className="rounded-none bg-[#1E2C5A] hover:bg-[#141F45] mt-6" onClick={submit} data-testid="save-layer">
          {mode === "edit" ? "Save Changes" : "Add Tile Layer"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("dtile_admin_token"));
  const [me, setMe] = useState(null);

  useEffect(() => {
    if (!authed) return;
    api.get("/auth/admin/me")
      .then(r => setMe(r.data))
      .catch(() => setAuthed(false));
  }, [authed]);

  const logout = () => {
    localStorage.removeItem("dtile_admin_token");
    setAuthed(false);
    setMe(null);
  };

  if (!authed) {
    return <AdminLogin onSuccess={() => setAuthed(true)} />;
  }
  if (!me) {
    return <div className="p-20 text-center text-[#8A8A8A]">Loading admin session…</div>;
  }

  return <AdminDashboard onLogout={logout} me={me} />;
}

// Permission helper — super admin always passes.
function hasPerm(me, key) {
  if (!me) return false;
  if (me.is_super) return true;
  const perms = me.permissions || {};
  return !!perms[key];
}

function AdminDashboard({ onLogout, me }) {
  // Auto-logout after 60 minutes of inactivity ---------------------------
  useEffect(() => {
    const IDLE_MS = 60 * 60 * 1000; // 1 hour
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        toast.info("Session expired · signed out for security");
        onLogout();
      }, IDLE_MS);
    };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach(e => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, reset));
    };
  }, [onLogout]);

  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [layers, setLayers] = useState([]);
  const [poMap, setPoMap] = useState({});
  const [referrals, setReferrals] = useState([]);
  const [completed, setCompleted] = useState({ count: 0, total_revenue: 0, total_boxes: 0, unique_customers: 0, orders: [] });
  const [dashboard, setDashboard] = useState(null);
  const [period, setPeriod] = useState("daily");

  const [buyers, setBuyers] = useState([]);
  const [buyerFilter, setBuyerFilter] = useState("pending");
  const [buyerLocationFilter, setBuyerLocationFilter] = useState("all");
  const [buyerLocations, setBuyerLocations] = useState([]);
  const [referralPeriod, setReferralPeriod] = useState("all");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [tileTypes, setTileTypes] = useState([]);
  const [productTiers, setProductTiers] = useState([]);
  const [newTileTypeName, setNewTileTypeName] = useState("");
  const [orderStatusFilter, setOrderStatusFilter] = useState("all");
  const [referralSearch, setReferralSearch] = useState("");
  const [layerSearch, setLayerSearch] = useState("");
  const [adminReturns, setAdminReturns] = useState([]);
  const [addBuyerDlg, setAddBuyerDlg] = useState(false);
  const [discountDlg, setDiscountDlg] = useState({ open: false, order: null });
  const [otherCostDlg, setOtherCostDlg] = useState({ open: false, order: null });
  const [newOrderDlg, setNewOrderDlg] = useState(false);
  const [creditsDlg, setCreditsDlg] = useState({ open: false, buyer: null });

  const [stockCheckOrders, setStockCheckOrders] = useState([]);
  const [editOrderDlg, setEditOrderDlg] = useState({ open: false, order: null });
  const [dispatchDlg, setDispatchDlg] = useState({ open: false, order: null });
  const [deliveryPhotosDlg, setDeliveryPhotosDlg] = useState({ open: false, order: null });

  // One-click asc/desc toggles for admin lists
  const sortProducts = useSortDir("asc");
  const [productSearch, setProductSearch] = useState("");
  const sortVendors = useSortDir();
  const sortBuyers = useSortDir();
  const sortTileTypes = useSortDir();
  const sortReferrals = useSortDir();
  const sortLayers = useSortDir();

  const [productDlg, setProductDlg] = useState({ open: false, mode: "create", data: null });
  const [layerDlg, setLayerDlg] = useState({ open: false, mode: "create", data: null });
  const [vendorDlg, setVendorDlg] = useState({ open: false, mode: "create", data: null });
  const [uploadDlg, setUploadDlg] = useState({ open: false, kind: null });

  const load = useCallback(() => {
    api.get("/products").then(r => setProducts(r.data)).catch(() => {});
    api.get("/orders").then(r => setOrders(r.data));
    api.get("/tile-layers").then(r => setLayers(r.data));
    api.get("/admin/completed-business").then(r => setCompleted(r.data));
    api.get("/vendors").then(r => setVendors(r.data));
    api.get("/tile-types").then(r => setTileTypes(r.data));
    api.get("/product-tiers").then(r => setProductTiers(r.data));
    api.get("/admin/buyer-locations").then(r => setBuyerLocations(r.data)).catch(() => {});
    api.get("/admin/selected-products").then(r => setSelectedProducts(r.data)).catch(() => {});
    api.get("/admin/orders/stock-check").then(r => setStockCheckOrders(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/admin/dashboard", { params: { period } }).then(r => setDashboard(r.data));
  }, [period]);

  useEffect(() => {
    const params = {};
    if (buyerFilter !== "all") params.status = buyerFilter;
    if (buyerLocationFilter && buyerLocationFilter !== "all") params.location = buyerLocationFilter;
    api.get("/admin/buyers", { params }).then(r => setBuyers(r.data));
  }, [buyerFilter, buyerLocationFilter]);

  useEffect(() => {
    const params = referralPeriod === "all" ? {} : { period: referralPeriod };
    api.get("/admin/manual-referrals", { params }).then(r => setReferrals(r.data));
  }, [referralPeriod]);

  const saveProduct = async (payload) => {
    try {
      if (productDlg.mode === "edit") {
        await api.patch(`/products/${productDlg.data.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created");
      }
      setProductDlg({ open: false, mode: "create", data: null });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const delProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    load();
  };

  const saveLayer = async (payload) => {
    try {
      if (layerDlg.mode === "edit") {
        await api.patch(`/tile-layers/${layerDlg.data.id}`, payload);
        toast.success("Tile layer updated");
      } else {
        await api.post("/tile-layers", payload);
        toast.success("Tile layer added");
      }
      setLayerDlg({ open: false, mode: "create", data: null });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const delLayer = async (id) => {
    if (!window.confirm("Delete this tile layer?")) return;
    await api.delete(`/tile-layers/${id}`);
    toast.success("Deleted");
    load();
  };

  const saveVendor = async (payload) => {
    try {
      if (vendorDlg.mode === "edit") {
        await api.patch(`/vendors/${vendorDlg.data.id}`, payload);
        toast.success("Vendor updated");
      } else {
        await api.post("/vendors", payload);
        toast.success("Vendor added");
      }
      setVendorDlg({ open: false, mode: "create", data: null });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const delVendor = async (id) => {
    if (!window.confirm("Delete this vendor?")) return;
    await api.delete(`/vendors/${id}`);
    toast.success("Deleted");
    load();
  };

  const delOrder = async (o) => {
    if (!window.confirm(`Permanently delete order ${o.order_no}?\n\nThis removes the order and its purchase orders. This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/orders/${o.id}`);
      toast.success(`Order ${o.order_no} deleted`);
      api.get("/orders").then(r => setOrders(r.data));
      api.get("/admin/orders/stock-check").then(r => setStockCheckOrders(r.data)).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const approveBuyer = async (id) => {
    await api.patch(`/admin/buyers/${id}/approve`);
    toast.success("Buyer approved");
    const params = buyerFilter === "all" ? {} : { status: buyerFilter };
    api.get("/admin/buyers", { params }).then(r => setBuyers(r.data));
  };

  const rejectBuyer = async (id) => {
    if (!window.confirm("Reject this buyer?")) return;
    await api.patch(`/admin/buyers/${id}/reject`);
    toast.success("Buyer rejected");
    const params = buyerFilter === "all" ? {} : { status: buyerFilter };
    api.get("/admin/buyers", { params }).then(r => setBuyers(r.data));
  };

  const downloadTemplate = (kind) => {
    const token = localStorage.getItem("dtile_admin_token");
    const url = `${API}/imports/${kind}/template`;
    // GET with no auth is fine for templates but we route via api instance for consistency
    api.get(`/imports/${kind}/template`, { responseType: "blob" }).then(res => {
      const blob = new Blob([res.data], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${kind}_template.csv`;
      link.click();
    });
  };

  const uploadCSV = async (kind, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/imports/${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const errMsg = data.errors?.length ? ` · ${data.errors.length} errors` : "";
      toast.success(`Created ${data.created}${data.updated ? ", updated " + data.updated : ""}${errMsg}`);
      setUploadDlg({ open: false, kind: null });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    }
  };

  const createTileType = async (name) => {
    try {
      const { data } = await api.post("/tile-types", { name });
      toast.success(`Tile type "${data.name}" ready`);
      const r = await api.get("/tile-types");
      setTileTypes(r.data);
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create tile type");
      return null;
    }
  };

  const deleteTileType = async (id) => {
    if (!window.confirm("Delete this tile type?")) return;
    await api.delete(`/tile-types/${id}`);
    toast.success("Deleted");
    const r = await api.get("/tile-types");
    setTileTypes(r.data);
  };

  const createProductTier = async (name) => {
    try {
      const { data } = await api.post("/product-tiers", { name });
      toast.success(`Product tier "${data.name}" ready`);
      const r = await api.get("/product-tiers");
      setProductTiers(r.data);
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create tier");
      return null;
    }
  };

  const deleteProductTier = async (id) => {
    if (!window.confirm("Delete this product tier?")) return;
    await api.delete(`/product-tiers/${id}`);
    toast.success("Deleted");
    const r = await api.get("/product-tiers");
    setProductTiers(r.data);
  };

  const setBuyerAccess = async (buyerId, level) => {
    try {
      await api.patch(`/admin/buyers/${buyerId}/access`, { access_level: level });
      toast.success(`Access set to ${level}`);
      const params = {};
      if (buyerFilter !== "all") params.status = buyerFilter;
      if (buyerLocationFilter && buyerLocationFilter !== "all") params.location = buyerLocationFilter;
      const r = await api.get("/admin/buyers", { params });
      setBuyers(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  // Orders filtered by admin+dispatch status
  const filteredOrders = orders.filter(o => {
    if (orderStatusFilter === "all") return true;
    if (["pending", "approved", "rejected"].includes(orderStatusFilter)) {
      return (o.admin_status || "pending") === orderStatusFilter;
    }
    return o.dispatch_status === orderStatusFilter;
  });

  // Free-text filter across every scalar attribute
  const matchAny = (obj, q) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return Object.values(obj || {}).some(v => {
      if (v == null) return false;
      if (typeof v === "object") return JSON.stringify(v).toLowerCase().includes(needle);
      return String(v).toLowerCase().includes(needle);
    });
  };
  const filteredReferrals = referrals.filter(r => matchAny(r, referralSearch));
  const filteredLayers = layers.filter(l => matchAny(l, layerSearch));

  const renameTileType = async (id, name) => {
    if (!name.trim()) return;
    await api.patch(`/tile-types/${id}`, { name: name.trim() });
    toast.success("Updated");
    const r = await api.get("/tile-types");
    setTileTypes(r.data);
  };

  const loadPOs = async (orderId) => {
    const { data } = await api.get(`/orders/${orderId}/purchase-orders`);
    setPoMap(prev => ({ ...prev, [orderId]: data }));
  };

  const advance = async (id) => {
    await api.post(`/orders/${id}/dispatch`);
    load();
  };

  const approveOrder = async (id) => {
    await api.patch(`/admin/orders/${id}/approve`);
    toast.success("Order approved — dispatch started");
    load();
  };

  const rejectOrder = async (id) => {
    if (!window.confirm("Reject this order? Customer will be refunded.")) return;
    await api.patch(`/admin/orders/${id}/reject`);
    toast.success("Order rejected");
    load();
  };

  return (
    <div className="bg-white min-h-screen" data-testid="admin-page">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-10">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-8" data-testid="admin-header">
          <div className="min-w-0">
            <p className="label-eyebrow">Command Center</p>
            <h1 className="font-editorial text-3xl md:text-4xl mt-2">Admin Dashboard</h1>
            <p className="text-xs text-[#5C5C5C] mt-1" data-testid="admin-header-user">
              Signed in as <span className="font-medium">{me.email || me.username}</span> ·
              <span className="ml-1 uppercase tracking-wider text-[#1E2C5A]">{me.role_name || (me.is_super ? "Super Admin" : "Staff")}</span>
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 lg:justify-end lg:flex-nowrap lg:gap-8 shrink-0">
            <div className="flex items-baseline gap-5 lg:gap-6 text-right pr-1 border-r border-[#1A1A1A]/10">
              <div>
                <p className="label-eyebrow whitespace-nowrap">Products</p>
                <p className="text-2xl xl:text-3xl font-editorial tabular-nums" data-testid="stat-products">{products.length}</p>
              </div>
              <div>
                <p className="label-eyebrow whitespace-nowrap">Orders</p>
                <p className="text-2xl xl:text-3xl font-editorial tabular-nums" data-testid="stat-orders">{orders.length}</p>
              </div>
              <div>
                <p className="label-eyebrow whitespace-nowrap">Layers</p>
                <p className="text-2xl xl:text-3xl font-editorial tabular-nums" data-testid="stat-layers">{layers.length}</p>
              </div>
            </div>
            <button onClick={onLogout} className="text-xs uppercase tracking-[0.2em] text-[#5C5C5C] hover:text-[#1E2C5A] inline-flex items-center gap-1 whitespace-nowrap" data-testid="admin-logout">
              <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} /> Sign Out
            </button>
            {me.is_super && (
              <button
                onClick={async () => {
                  const tok = localStorage.getItem("dtile_admin_token");
                  toast.info("Preparing master export…");
                  try {
                    const resp = await fetch(`${API}/admin/master-export`, {
                      headers: { Authorization: `Bearer ${tok}` },
                    });
                    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                    const blob = await resp.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `TileRoom_Master_Backup_${new Date().toISOString().slice(0,10)}.zip`;
                    document.body.appendChild(a); a.click(); a.remove();
                    URL.revokeObjectURL(url);
                    toast.success("Master export downloaded");
                  } catch (e) {
                    toast.error("Export failed");
                  }
                }}
                className="text-xs uppercase tracking-[0.2em] text-[#C9A968] hover:text-[#1E2C5A] inline-flex items-center gap-1 border border-[#C9A968] px-3 py-1.5 hover:bg-[#C9A968] transition-colors whitespace-nowrap"
                data-testid="master-export-btn"
                title="Downloads a ZIP with CSV + Excel + JSON + branded PDF for the entire database"
              >
                <Download className="w-3.5 h-3.5" strokeWidth={1.5} /> Master Export
              </button>
            )}
          </div>
        </div>

        <Tabs defaultValue="dashboard" className="w-full" data-testid="admin-tabs">
          <TabsList className="flex overflow-x-auto rounded-none bg-[#F7F7F8] p-0 h-auto justify-start w-full">
            {hasPerm(me, "mod.dashboard") && <TabsTrigger value="dashboard" className="rounded-none flex-shrink-0 px-5 py-3" data-testid="tab-dashboard">Dashboard</TabsTrigger>}
            {hasPerm(me, "mod.products") && <TabsTrigger value="products" className="rounded-none flex-shrink-0 px-5 py-3" data-testid="tab-products">Products</TabsTrigger>}
            {hasPerm(me, "mod.numbering") && <TabsTrigger value="numbering" className="rounded-none flex-shrink-0 px-5 py-3" data-testid="tab-numbering">Numbering</TabsTrigger>}
            {hasPerm(me, "mod.tile-types") && <TabsTrigger value="tile-types" className="rounded-none flex-shrink-0 px-5 py-3" data-testid="tab-tile-types">Tile Types</TabsTrigger>}
            {hasPerm(me, "mod.vendors") && <TabsTrigger value="vendors" className="rounded-none flex-shrink-0 px-5 py-3" data-testid="tab-vendors">Vendors</TabsTrigger>}
            {hasPerm(me, "mod.buyers") && <TabsTrigger value="buyers" className="rounded-none flex-shrink-0 px-5 py-3" data-testid="tab-buyers">Buyers</TabsTrigger>}
            {hasPerm(me, "mod.orders") && <TabsTrigger value="orders" className="rounded-none flex-shrink-0 px-5 py-3" data-testid="tab-orders">Orders</TabsTrigger>}
            {hasPerm(me, "mod.check-stock") && (
              <TabsTrigger value="check-stock" className="rounded-none flex-shrink-0 px-5 py-3 relative" data-testid="tab-check-stock">
                Check Stock
                {stockCheckOrders.length > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-medium bg-[#C9A968] text-white rounded-full" data-testid="check-stock-count">
                    {stockCheckOrders.length}
                  </span>
                )}
              </TabsTrigger>
            )}
            {hasPerm(me, "mod.returns") && <TabsTrigger value="returns" className="rounded-none flex-shrink-0 px-5 py-3" data-testid="tab-returns">Returns</TabsTrigger>}
            {hasPerm(me, "mod.completed") && <TabsTrigger value="completed" className="rounded-none flex-shrink-0 px-5 py-3" data-testid="tab-completed">Completed</TabsTrigger>}
            {hasPerm(me, "mod.selected") && <TabsTrigger value="selected" className="rounded-none flex-shrink-0 px-5 py-3" data-testid="tab-selected">Selected Products</TabsTrigger>}
            {hasPerm(me, "mod.referrals") && <TabsTrigger value="referrals" className="rounded-none flex-shrink-0 px-5 py-3" data-testid="tab-referrals">Referrals</TabsTrigger>}
            {hasPerm(me, "mod.layers") && <TabsTrigger value="layers" className="rounded-none flex-shrink-0 px-5 py-3" data-testid="tab-layers">Tile Layers</TabsTrigger>}
            {hasPerm(me, "mod.to-arrange") && <TabsTrigger value="to-arrange" className="rounded-none flex-shrink-0 px-5 py-3" data-testid="tab-to-arrange">To Arrange Goods</TabsTrigger>}
            {hasPerm(me, "mod.pending-deliveries") && <TabsTrigger value="pending-deliveries" className="rounded-none flex-shrink-0 px-5 py-3" data-testid="tab-pending-deliveries">Pending Deliveries</TabsTrigger>}
            {hasPerm(me, "mod.quotes") && <TabsTrigger value="quotes" className="rounded-none flex-shrink-0 px-5 py-3" data-testid="tab-quotes">Quotes</TabsTrigger>}
            {hasPerm(me, "mod.appointments") && <TabsTrigger value="appointments" className="rounded-none flex-shrink-0 px-5 py-3" data-testid="tab-appointments">Appointments</TabsTrigger>}
            {hasPerm(me, "mod.reports") && <TabsTrigger value="reports" className="rounded-none flex-shrink-0 px-5 py-3" data-testid="tab-reports">Reports</TabsTrigger>}
            {me.is_super && <TabsTrigger value="payment-qr" className="rounded-none flex-shrink-0 px-5 py-3" data-testid="tab-payment-qr">Payment QR</TabsTrigger>}
            {me.is_super && <TabsTrigger value="payment-verify" className="rounded-none flex-shrink-0 px-5 py-3" data-testid="tab-payment-verify">Payment Verification</TabsTrigger>}
            {me.is_super && <TabsTrigger value="users" className="rounded-none flex-shrink-0 px-5 py-3" data-testid="tab-users">Users</TabsTrigger>}
            {me.is_super && <TabsTrigger value="employees" className="rounded-none flex-shrink-0 px-5 py-3" data-testid="tab-employees">Employees</TabsTrigger>}
            {me.is_super && <TabsTrigger value="announcements" className="rounded-none flex-shrink-0 px-5 py-3" data-testid="tab-announcements">Announcements</TabsTrigger>}
          </TabsList>

          {/* DASHBOARD */}
          <TabsContent value="dashboard" className="mt-8">
            <div className="flex items-center justify-between mb-6">
              <p className="label-eyebrow">Goods Outward Movement</p>
              <div className="flex gap-2" data-testid="period-selector">
                {["daily","weekly","monthly","yearly"].map(p => (
                  <button
                    key={p}
                    onClick={() => setPeriod(p)}
                    className={`px-4 py-2 text-xs uppercase tracking-[0.15em] border transition-colors ${
                      period === p
                        ? "bg-[#1E2C5A] text-white border-[#1E2C5A]"
                        : "border-[#1A1A1A]/20 text-[#5C5C5C] hover:border-[#1E2C5A]"
                    }`}
                    data-testid={`period-${p}`}
                  >{p}</button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              <div className="border border-[#1A1A1A]/10 p-6">
                <p className="label-eyebrow">Total Orders</p>
                <p className="font-editorial text-5xl mt-2" data-testid="metric-orders">{dashboard?.totals?.orders ?? 0}</p>
              </div>
              <div className="border border-[#1A1A1A]/10 p-6">
                <p className="label-eyebrow">Sizes Moved</p>
                <p className="font-editorial text-5xl mt-2" data-testid="metric-sizes">{dashboard?.totals?.unique_sizes ?? 0}</p>
              </div>
              <div className="border border-[#1A1A1A]/10 p-6">
                <p className="label-eyebrow">Revenue (₹)</p>
                <p className="font-editorial text-5xl mt-2 text-[#1E2C5A]" data-testid="metric-revenue">
                  {(dashboard?.totals?.revenue ?? 0).toLocaleString("en-IN")}
                </p>
              </div>
            </div>

            {/* Size-wise breakdown */}
            <div className="border border-[#1A1A1A]/10 p-6 mb-10" data-testid="dashboard-chart">
              <p className="label-eyebrow mb-6">Boxes dispatched by size — {period}</p>
              {dashboard?.size_breakdown?.length > 0 ? (
                <div className="space-y-4">
                  {(() => {
                    const max = Math.max(1, ...dashboard.size_breakdown.map(s => s.boxes));
                    return dashboard.size_breakdown.map((s) => (
                      <div key={`${s.size}-${s.tile_type}`} className="grid grid-cols-12 gap-4 items-center" data-testid={`size-row-${s.size}-${s.tile_type}`}>
                        <div className="col-span-3">
                          <p className="font-medium text-sm">{s.size}</p>
                          <p className="text-[10px] uppercase tracking-[0.15em] text-[#1E2C5A]">{s.tile_type}</p>
                        </div>
                        <div className="col-span-6 relative">
                          <div className="h-6 bg-[#F0EFEA]" />
                          <div
                            className="absolute inset-y-0 left-0 bg-[#1E2C5A]"
                            style={{ width: `${(s.boxes / max) * 100}%` }}
                          />
                        </div>
                        <div className="col-span-1 text-right font-editorial text-xl">{s.boxes}</div>
                        <div className="col-span-1 text-xs text-[#5C5C5C] text-right">{s.orders} ord</div>
                        <div className="col-span-1 text-xs text-[#1E2C5A] font-medium text-right">₹{s.revenue.toLocaleString("en-IN")}</div>
                      </div>
                    ));
                  })()}
                </div>
              ) : <p className="text-[#8A8A8A] text-sm">No outward movement in this period.</p>}
            </div>

            {/* Size breakdown table (detailed) */}
            <div>
              <p className="label-eyebrow mb-4">Size-wise Details ({period})</p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Size</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Boxes Dispatched</TableHead>
                    <TableHead>Orders</TableHead>
                    <TableHead>Revenue</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dashboard?.size_breakdown?.length ? dashboard.size_breakdown.map((s) => (
                    <TableRow key={`${s.size}-${s.tile_type}`} data-testid={`size-detail-${s.size}-${s.tile_type}`}>
                      <TableCell className="font-medium">{s.size}</TableCell>
                      <TableCell><span className="text-xs uppercase tracking-wider">{s.tile_type}</span></TableCell>
                      <TableCell>{s.boxes}</TableCell>
                      <TableCell>{s.orders}</TableCell>
                      <TableCell className="text-[#1E2C5A] font-medium">₹{s.revenue.toLocaleString("en-IN")}</TableCell>
                    </TableRow>
                  )) : (
                    <TableRow><TableCell colSpan={5} className="text-[#8A8A8A] text-sm text-center py-6">No dispatches in this period.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* PRODUCTS */}
          <TabsContent value="products" className="mt-8">
            <div className="flex justify-between mb-4 flex-wrap gap-2">
              <div className="flex gap-2">
                <ExportButton
                  rows={products.map(p => ({
                    our_code: p.our_code,
                    name: p.name,
                    size: p.size,
                    tile_type: p.tile_type,
                    product_tier: p.product_tier,
                    category: p.category,
                    uom: p.primary_uom,
                    packing: p.packing,
                    mrp: p.mrp,
                    offer_price: p.offer_price,
                    scode: p.scode,
                    adc: p.adc,
                  }))}
                  name="products"
                  title="TileRoom Products"
                  testId="export-products"
                />
                <Button
                  variant="outline"
                  className="rounded-none border-[#1E2C5A] text-[#1E2C5A] hover:bg-[#1E2C5A] hover:text-white"
                  onClick={() => downloadTemplate("products")}
                  data-testid="download-product-template"
                >
                  <Download className="w-4 h-4 mr-1" strokeWidth={1.5} /> CSV Template
                </Button>
                <Button
                  variant="outline"
                  className="rounded-none border-[#C9A968] text-[#1E2C5A] hover:bg-[#C9A968]"
                  onClick={() => setUploadDlg({ open: true, kind: "products" })}
                  data-testid="bulk-upload-products"
                >
                  <Upload className="w-4 h-4 mr-1" strokeWidth={1.5} /> Bulk Upload
                </Button>
              </div>
              <Button
                className="rounded-none bg-[#1E2C5A] hover:bg-[#141F45]"
                onClick={() => setProductDlg({ open: true, mode: "create", data: null })}
                data-testid="new-product-btn"
              >
                <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} /> New Product
              </Button>
            </div>

            <div className="flex justify-between items-center gap-3 mb-3 flex-wrap">
              <div className="relative flex-1 min-w-[240px] max-w-md">
                <Input
                  className="rounded-none pl-9"
                  placeholder="Search by code, name, size, category…"
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  data-testid="product-search"
                />
                <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A8A]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.34-4.34m0 0A7.5 7.5 0 1 0 6.16 6.16a7.5 7.5 0 0 0 10.5 10.5Z" />
                </svg>
                {productSearch && (
                  <button
                    onClick={() => setProductSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8A8A8A] hover:text-[#1E2C5A] text-xs"
                    data-testid="product-search-clear"
                    aria-label="Clear search"
                  >×</button>
                )}
              </div>
              <SortToggle dir={sortProducts.dir} onToggle={sortProducts.toggle} testId="sort-products" />
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code / Name</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>UOM</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Packing</TableHead>
                  <TableHead>MRP</TableHead>
                  <TableHead>Offer</TableHead>
                  <TableHead>SCODE/ADC</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  const q = productSearch.trim().toLowerCase();
                  const filtered = q
                    ? products.filter(p =>
                        [p.our_code, p.name, p.size, p.category, p.tile_type, p.surface, p.colour, p.scode, p.adc]
                          .filter(Boolean)
                          .some(v => String(v).toLowerCase().includes(q))
                      )
                    : products;
                  const sorted = [...filtered].sort((a, b) =>
                    String(a.our_code || "").localeCompare(String(b.our_code || ""), undefined, { sensitivity: "base", numeric: true })
                  );
                  if (sortProducts.dir === "desc") sorted.reverse();
                  if (sorted.length === 0) {
                    return (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center text-sm text-[#8A8A8A] py-8" data-testid="product-search-empty">
                          {q ? `No products match "${productSearch}"` : "No products yet"}
                        </TableCell>
                      </TableRow>
                    );
                  }
                  return sorted.map(p => (
                  <TableRow key={p.id} data-testid={`admin-product-${p.our_code}`}>
                    <TableCell className="font-medium">{p.our_code}</TableCell>
                    <TableCell>{p.size}</TableCell>
                    <TableCell className="text-xs uppercase">{p.tile_type || "floor"}</TableCell>
                    <TableCell className="text-xs">
                      <span className="text-[#1E2C5A] font-medium">{p.primary_uom || "BOX"}</span>
                      <span className="text-[#8A8A8A]"> / {p.secondary_uom || "SQ.FT"}</span>
                    </TableCell>
                    <TableCell>{p.category}</TableCell>
                    <TableCell>{p.packing}</TableCell>
                    <TableCell>₹{p.mrp}</TableCell>
                    <TableCell className="text-[#1E2C5A] font-medium">₹{p.offer_price}</TableCell>
                    <TableCell className="text-xs">{p.scode || p.adc || <span className="text-[#8A8A8A]">warehouse</span>}</TableCell>
                    <TableCell className="space-x-3 whitespace-nowrap">
                      <button
                        onClick={() => setProductDlg({ open: true, mode: "edit", data: p })}
                        className="text-[#1E2C5A] hover:text-[#141F45] inline-flex items-center gap-1 text-xs"
                        data-testid={`edit-product-${p.our_code}`}
                      >
                        <Pencil className="w-4 h-4" strokeWidth={1.5} /> Edit
                      </button>
                      <button
                        onClick={() => delProduct(p.id)}
                        className="text-red-700 hover:text-red-900 inline-flex items-center gap-1 text-xs"
                        data-testid={`del-product-${p.our_code}`}
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} /> Delete
                      </button>
                    </TableCell>
                  </TableRow>
                  ));
                })()}
              </TableBody>
            </Table>
          </TabsContent>

          {/* NUMBERING MASTER */}
          <TabsContent value="numbering" className="mt-8">
            <NumberingMasterPanel />
            {me.is_super && <DocNumberSettingsPanel />}
          </TabsContent>

          {/* PAYMENT QR */}
          <TabsContent value="payment-qr" className="mt-8">
            <PaymentQRPanel />
          </TabsContent>

          {/* PAYMENT VERIFICATION */}
          <TabsContent value="payment-verify" className="mt-8">
            <PaymentVerificationPanel />
          </TabsContent>

          {/* ORDERS */}
          <TabsContent value="orders" className="mt-8">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <div className="flex gap-2 flex-wrap" data-testid="order-status-filter">
                {[
                  { k: "all", l: "All" },
                  { k: "pending", l: "Pending" },
                  { k: "approved", l: "Approved" },
                  { k: "rejected", l: "Rejected" },
                  { k: "packed", l: "Packed" },
                  { k: "shipped", l: "Shipped" },
                  { k: "out_for_delivery", l: "Out for Delivery" },
                  { k: "delivered", l: "Delivered" },
                ].map(f => (
                  <button
                    key={f.k}
                    onClick={() => setOrderStatusFilter(f.k)}
                    className={`px-4 py-2 text-xs uppercase tracking-[0.15em] border transition-colors ${
                      orderStatusFilter === f.k ? "bg-[#1E2C5A] text-white border-[#1E2C5A]" : "border-[#1A1A1A]/20 text-[#5C5C5C]"
                    }`}
                    data-testid={`order-filter-${f.k}`}
                  >{f.l}</button>
                ))}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className="inline-flex items-center gap-1 bg-[#1E2C5A] text-[#C9A968] px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] border border-[#C9A968]/40"
                  title="Indian Financial Year — resets automatically on 1 April"
                  data-testid="orders-fy-badge"
                >
                  FY {(new Date().getMonth() >= 3
                    ? new Date().getFullYear() % 100
                    : (new Date().getFullYear() - 1) % 100
                  ).toString().padStart(2,'0')}-{((new Date().getMonth() >= 3
                    ? new Date().getFullYear() % 100
                    : (new Date().getFullYear() - 1) % 100) + 1).toString().padStart(2,'0')}
                </span>
                <button
                  onClick={async () => {
                    try {
                      const res = await api.get("/admin/documents/current-fy.zip", { responseType: "blob" });
                      const url = window.URL.createObjectURL(new Blob([res.data]));
                      const a = document.createElement("a");
                      a.href = url; a.download = "TileRoom_CurrentFY_documents.zip";
                      document.body.appendChild(a); a.click(); a.remove();
                      window.URL.revokeObjectURL(url);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "No documents for the current FY yet");
                    }
                  }}
                  className="inline-flex items-center gap-1 border border-[#C9A968] text-[#C9A968] hover:bg-[#C9A968] hover:text-white px-3 py-1.5 text-xs uppercase tracking-[0.15em] transition-colors"
                  title="Bundle every invoice + PO + challan for the current FY into one ZIP"
                  data-testid="orders-download-fy-zip"
                >
                  <Download className="w-3.5 h-3.5" strokeWidth={1.5} /> FY ZIP
                </button>
                <Button
                  className="rounded-none bg-[#1E2C5A] hover:bg-[#141F45]"
                  onClick={() => setNewOrderDlg(true)}
                  data-testid="admin-new-order-btn"
                >
                  <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} /> New Order (on-behalf)
                </Button>
                <ExportButton
                  rows={filteredOrders.map(o => ({
                    order_no: o.order_no,
                    customer: o.customer_name,
                    whatsapp: o.customer_whatsapp,
                    items: o.items.length,
                    total: o.total,
                    discount: o.additional_discount_value || 0,
                    grand_total: o.grand_total ?? o.total,
                    payment: o.payment_status,
                    admin_status: o.admin_status || "pending",
                    dispatch_status: o.dispatch_status,
                    created_at: o.created_at,
                  }))}
                  name="orders"
                  title="TileRoom Orders"
                  testId="export-orders"
                />
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Admin</TableHead>
                  <TableHead>Dispatch</TableHead>
                  <TableHead>Docs</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.map(o => (
                  <>
                    <TableRow key={o.id} data-testid={`admin-order-${o.order_no}`}>
                      <TableCell className="font-medium">{o.order_no}</TableCell>
                      <TableCell>{o.customer_name}<br/><span className="text-xs text-[#8A8A8A]">{o.customer_whatsapp}</span></TableCell>
                      <TableCell>{o.items.length}</TableCell>
                      <TableCell>
                        <div>₹{o.total.toFixed(0)}</div>
                        {o.grand_total != null && o.grand_total !== o.total && (
                          <div className="text-xs">
                            <span className="text-red-700">− ₹{(o.total - o.grand_total).toFixed(0)}</span>
                            <br/>
                            <span className="text-[#C9A968] font-medium">₹{o.grand_total.toFixed(0)}</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell><span className={o.payment_status === "paid" ? "text-green-700" : "text-[#1E2C5A]"}>{o.payment_status}</span></TableCell>
                      <TableCell className="text-xs">
                        <div className="flex flex-col gap-1">
                          <span className={
                            (o.admin_status || "pending") === "approved" ? "uppercase tracking-wider text-green-700" :
                            (o.admin_status || "pending") === "rejected" ? "uppercase tracking-wider text-red-700" :
                            "uppercase tracking-wider text-[#C9A968]"
                          }>{o.admin_status || "pending"}</span>
                          {(o.admin_status || "pending") === "pending" && o.payment_status === "paid" && (
                            <div className="flex gap-2">
                              <button onClick={() => approveOrder(o.id)} className="text-green-700 hover:underline" data-testid={`approve-order-${o.order_no}`}>Approve</button>
                              <button onClick={() => rejectOrder(o.id)} className="text-red-700 hover:underline" data-testid={`reject-order-${o.order_no}`}>Reject</button>
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs uppercase tracking-wider">{o.dispatch_status.replace(/_/g, " ")}</span>
                        <button onClick={() => advance(o.id)} className="ml-2 text-xs text-[#1E2C5A] hover:underline" data-testid={`admin-advance-${o.order_no}`}>advance</button>
                      </TableCell>
                      <TableCell className="text-xs space-x-2 whitespace-nowrap">
                        {(() => {
                          const ready = !!((o.vehicle_no || "").trim() && (o.driver_mobile || "").trim());
                          const disabledCls = "inline-flex items-center gap-1 text-[#B8B8B8] cursor-not-allowed";
                          const enabledCls = "inline-flex items-center gap-1 text-[#1E2C5A] hover:underline";
                          const notReadyTip = "Fill Vehicle No. & Driver Mobile under Dispatch Info first";
                          return (
                            <>
                              {ready ? (
                                <a href={`${API}/orders/${o.id}/challan`} target="_blank" rel="noreferrer" className={enabledCls} data-testid={`challan-${o.order_no}`}><Download className="w-3 h-3" strokeWidth={1.5} /> Challan</a>
                              ) : (
                                <span
                                  className={disabledCls}
                                  title={notReadyTip}
                                  onClick={() => toast.error(notReadyTip)}
                                  data-testid={`challan-disabled-${o.order_no}`}
                                ><Download className="w-3 h-3" strokeWidth={1.5} /> Challan</span>
                              )}
                              {ready ? (
                                <a href={`${API}/orders/${o.id}/invoice`} target="_blank" rel="noreferrer" className={enabledCls} data-testid={`invoice-${o.order_no}`}><Download className="w-3 h-3" strokeWidth={1.5} /> Invoice</a>
                              ) : (
                                <span
                                  className={disabledCls}
                                  title={notReadyTip}
                                  onClick={() => toast.error(notReadyTip)}
                                  data-testid={`invoice-disabled-${o.order_no}`}
                                ><Download className="w-3 h-3" strokeWidth={1.5} /> Invoice</span>
                              )}
                              {ready ? (
                                <button onClick={() => loadPOs(o.id)} className={enabledCls} data-testid={`po-btn-${o.order_no}`}><FileText className="w-3 h-3" strokeWidth={1.5} /> P.O</button>
                              ) : (
                                <button
                                  className={disabledCls}
                                  title={notReadyTip}
                                  onClick={() => toast.error(notReadyTip)}
                                  data-testid={`po-disabled-${o.order_no}`}
                                ><FileText className="w-3 h-3" strokeWidth={1.5} /> P.O</button>
                              )}
                            </>
                          );
                        })()}
                        <button
                          onClick={() => setDispatchDlg({ open: true, order: o })}
                          className={((o.vehicle_no || "").trim() && (o.driver_mobile || "").trim())
                            ? "inline-flex items-center gap-1 text-green-700 hover:underline"
                            : "inline-flex items-center gap-1 text-[#C9A968] hover:underline font-medium"}
                          title={(o.vehicle_no ? `Vehicle: ${o.vehicle_no} · Driver: ${o.driver_mobile}` : "Set Vehicle No. & Driver Mobile")}
                          data-testid={`dispatch-info-${o.order_no}`}
                        >
                          <Truck className="w-3 h-3" strokeWidth={1.5} />
                          {((o.vehicle_no || "").trim() && (o.driver_mobile || "").trim()) ? "Dispatch ✓" : "Dispatch Info"}
                        </button>
                        <button onClick={() => setDeliveryPhotosDlg({ open: true, order: o })} className="inline-flex items-center gap-1 text-[#1E2C5A] hover:underline" title="Upload photos of the delivered goods" data-testid={`delivery-photos-${o.order_no}`}>
                          <Camera className="w-3 h-3" strokeWidth={1.5} />
                          {(o.delivery_photos || []).length > 0 ? `Photos (${(o.delivery_photos || []).length})` : "Upload Photos"}
                        </button>
                        <button onClick={() => setDiscountDlg({ open: true, order: o })} className="inline-flex items-center gap-1 text-[#C9A968] hover:underline" data-testid={`discount-order-${o.order_no}`}>Discount</button>
                        <button onClick={() => setOtherCostDlg({ open: true, order: o })} className="inline-flex items-center gap-1 text-[#1E2C5A] hover:underline" title="Add extra cost (freight top-up, handling, insurance…) — adds to grand total" data-testid={`other-cost-${o.order_no}`}>
                          Other Cost{Number(o.other_cost || 0) > 0 ? ` (₹${Number(o.other_cost).toFixed(0)})` : ""}
                        </button>
                        <button onClick={() => setEditOrderDlg({ open: true, order: o })} className="inline-flex items-center gap-1 text-[#1E2C5A] hover:underline" data-testid={`edit-order-${o.order_no}`}><Pencil className="w-3 h-3" strokeWidth={1.5} /> Edit</button>
                        <button onClick={() => delOrder(o)} className="inline-flex items-center gap-1 text-red-700 hover:underline" data-testid={`delete-order-${o.order_no}`}><Trash2 className="w-3 h-3" strokeWidth={1.5} /> Delete</button>
                      </TableCell>
                    </TableRow>
                    {poMap[o.id] && (
                      <TableRow className="bg-[#F7F7F8]">
                        <TableCell colSpan={7} className="text-xs" data-testid={`po-list-${o.order_no}`}>
                          <p className="uppercase tracking-wider text-[#8A8A8A] mb-2">{poMap[o.id].count} supplier(s):</p>
                          {poMap[o.id].suppliers.length === 0
                            ? <span className="text-[#8A8A8A]">No supplier items (warehouse stock).</span>
                            : (
                              <>
                                {poMap[o.id].suppliers.map(s => (
                                  <a key={s.supplier_code} href={`${API}/orders/${o.id}/purchase-order/${s.supplier_code}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mr-4 text-[#1E2C5A]" data-testid={`po-single-${o.order_no}-${s.supplier_code}`}>
                                    <Download className="w-3 h-3" strokeWidth={1.5} /> PO {s.supplier_code} ({s.items} items)
                                  </a>
                                ))}
                                {poMap[o.id].suppliers.length > 1 && (
                                  <a
                                    href={`${API}/orders/${o.id}/purchase-orders/download-all`}
                                    target="_blank" rel="noreferrer"
                                    className="inline-flex items-center gap-1 ml-2 px-2 py-1 border border-[#C9A968] text-[#C9A968] hover:bg-[#C9A968] hover:text-white transition-colors uppercase tracking-wider"
                                    data-testid={`po-download-all-${o.order_no}`}
                                  >
                                    <Download className="w-3 h-3" strokeWidth={1.5} /> Download all POs (.zip)
                                  </a>
                                )}
                              </>
                            )
                          }
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))}
              </TableBody>
            </Table>
          </TabsContent>

          {/* CHECK STOCK — async availability workflow */}
          <TabsContent value="check-stock" className="mt-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="label-eyebrow">Pending Availability Checks</p>
                <h3 className="font-editorial text-2xl mt-1">{stockCheckOrders.length} order(s) awaiting your response</h3>
              </div>
              <button
                onClick={() => api.get("/admin/orders/stock-check").then(r => setStockCheckOrders(r.data))}
                className="text-xs uppercase tracking-[0.2em] text-[#1E2C5A] hover:text-[#141F45]"
                data-testid="refresh-stock-check"
              >Refresh ↻</button>
            </div>
            {stockCheckOrders.length === 0 ? (
              <div className="border border-dashed border-[#1A1A1A]/15 p-16 text-center text-[#8A8A8A]" data-testid="no-stock-checks">
                No orders awaiting stock check right now.
              </div>
            ) : (
              <div className="space-y-6">
                {stockCheckOrders.map(o => (
                  <StockCheckCard
                    key={o.id}
                    order={o}
                    onReplied={() => {
                      api.get("/admin/orders/stock-check").then(r => setStockCheckOrders(r.data));
                      api.get("/orders").then(r => setOrders(r.data));
                    }}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* RETURNS */}
          <TabsContent value="returns" className="mt-8">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <p className="label-eyebrow">Return Requests</p>
              <ExportButton
                rows={adminReturns.map(r => ({
                  order_no: r.order_no,
                  customer: r.customer_name,
                  reason: r.reason,
                  status: r.status,
                  created_at: r.created_at,
                  images_count: (r.images || []).length,
                }))}
                name="returns"
                title="TileRoom Returns"
                testId="export-returns"
              />
            </div>
            <ReturnsAdminTab onRows={setAdminReturns} />
          </TabsContent>

          {/* COMPLETED BUSINESS */}
          <TabsContent value="completed" className="mt-8">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <p className="label-eyebrow">Successful Deliveries</p>
              <ExportButton
                rows={(completed.orders || []).map(o => ({
                  order_no: o.order_no,
                  delivered: o.created_at,
                  customer: o.customer_name,
                  whatsapp: o.customer_whatsapp,
                  location: o.customer_location,
                  boxes: o.total_boxes,
                  revenue: o.revenue,
                }))}
                name="completed-deliveries"
                title="TileRoom Completed Deliveries"
                testId="export-completed"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <div className="border border-[#1A1A1A]/10 p-5" data-testid="completed-count">
                <p className="label-eyebrow">Deliveries</p>
                <p className="font-editorial text-4xl mt-2">{completed.count}</p>
              </div>
              <div className="border border-[#1A1A1A]/10 p-5" data-testid="completed-revenue">
                <p className="label-eyebrow">Revenue</p>
                <p className="font-editorial text-4xl mt-2 text-[#1E2C5A]">₹{completed.total_revenue.toLocaleString("en-IN")}</p>
              </div>
              <div className="border border-[#1A1A1A]/10 p-5" data-testid="completed-boxes">
                <p className="label-eyebrow">Boxes</p>
                <p className="font-editorial text-4xl mt-2">{completed.total_boxes}</p>
              </div>
              <div className="border border-[#1A1A1A]/10 p-5" data-testid="completed-customers">
                <p className="label-eyebrow">Customers</p>
                <p className="font-editorial text-4xl mt-2">{completed.unique_customers}</p>
              </div>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order</TableHead>
                  <TableHead>Delivered</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Boxes</TableHead>
                  <TableHead>Revenue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {completed.orders?.length ? completed.orders.map(o => (
                  <TableRow key={o.id} data-testid={`completed-${o.order_no}`}>
                    <TableCell className="font-medium">{o.order_no}</TableCell>
                    <TableCell className="text-xs">{new Date(o.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>{o.customer_name}<br/><span className="text-xs text-[#8A8A8A]">{o.customer_whatsapp}</span></TableCell>
                    <TableCell className="text-xs">{o.customer_location}</TableCell>
                    <TableCell>{o.items?.reduce((s, it) => s + (it.quantity_boxes || 0), 0)}</TableCell>
                    <TableCell className="text-[#1E2C5A] font-medium">₹{o.total.toFixed(0)}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow><TableCell colSpan={6} className="text-center text-[#8A8A8A] py-6">No completed deliveries yet.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TabsContent>

          {/* SELECTED PRODUCTS (viewed but not confirmed) */}
          <TabsContent value="selected" className="mt-8">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
              <div>
                <p className="label-eyebrow">Selected Products (viewed but not confirmed)</p>
                <p className="text-xs text-[#8A8A8A] mt-1">Buyers who browsed a product but never checked out with it. Reach out to close the deal.</p>
              </div>
              <ExportButton
                rows={selectedProducts.map(s => ({
                  buyer: s.buyer_name,
                  company: s.buyer_company,
                  mobile: s.buyer_mobile,
                  product_code: s.product_code,
                  size: s.product_size,
                  offer_price: s.offer_price,
                  views: s.views,
                  last_viewed_at: s.last_viewed_at,
                }))}
                name="selected-products"
                title="TileRoom Selected Products"
                testId="export-selected"
              />
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Buyer</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Mobile</TableHead>
                  <TableHead>Product Code</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Views</TableHead>
                  <TableHead>Last Viewed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selectedProducts.length ? selectedProducts.map((s, i) => (
                  <TableRow key={s.id || i} data-testid={`selected-row-${i}`}>
                    <TableCell className="font-medium">{s.buyer_name || "—"}</TableCell>
                    <TableCell className="text-xs">{s.buyer_company || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{s.buyer_mobile || "—"}</TableCell>
                    <TableCell className="text-xs text-[#1E2C5A]">{s.product_code}</TableCell>
                    <TableCell className="text-xs">{s.product_size}</TableCell>
                    <TableCell className="text-xs">₹{Number(s.offer_price || 0).toFixed(0)}</TableCell>
                    <TableCell className="text-xs text-[#C9A968]">{s.views}</TableCell>
                    <TableCell className="text-xs text-[#8A8A8A]">{new Date(s.last_viewed_at).toLocaleString()}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow><TableCell colSpan={8} className="text-center text-[#8A8A8A] py-6">No unconfirmed product views yet.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TabsContent>

          {/* REFERRALS */}
          <TabsContent value="referrals" className="mt-8">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <p className="label-eyebrow">Manual Referrals (Buyer-Submitted)</p>
              <div className="flex gap-2 flex-wrap items-center">
                <Input
                  className="rounded-none w-64"
                  placeholder="Search any attribute..."
                  value={referralSearch}
                  onChange={(e) => setReferralSearch(e.target.value)}
                  data-testid="referral-search"
                />
                <ExportButton
                  rows={filteredReferrals.map(r => ({
                    date: r.referral_date,
                    referrer: r.referrer_name,
                    referrer_mobile: r.referrer_mobile,
                    referrer_company: r.referrer_company,
                    referred_name: r.referred_name,
                    referred_mobile: r.referred_mobile,
                    referred_location: r.referred_location,
                    comments: r.comments,
                  }))}
                  name="referrals"
                  title="TileRoom Referrals"
                  testId="export-referrals"
                />
              </div>
            </div>
            <div className="flex gap-2 flex-wrap mb-6" data-testid="referral-period-filter">
              {["all","today","week","month","year","past"].map(p => (
                <button
                  key={p}
                  onClick={() => setReferralPeriod(p)}
                  className={`px-4 py-2 text-xs uppercase tracking-[0.15em] border transition-colors ${
                    referralPeriod === p ? "bg-[#1E2C5A] text-white border-[#1E2C5A]" : "border-[#1A1A1A]/20 text-[#5C5C5C]"
                  }`}
                  data-testid={`ref-period-${p}`}
                >{p === "week" ? "This Week" : p === "month" ? "This Month" : p === "year" ? "This Year" : p === "past" ? "Past" : p === "all" ? "All" : "Today"}</button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="border border-[#1A1A1A]/10 p-5" data-testid="ref-total">
                <p className="label-eyebrow">Total Referrals</p>
                <p className="font-editorial text-4xl mt-2">{filteredReferrals.length}</p>
              </div>
              <div className="border border-[#1A1A1A]/10 p-5" data-testid="ref-referrers">
                <p className="label-eyebrow">Unique Referrers</p>
                <p className="font-editorial text-4xl mt-2">{new Set(filteredReferrals.map(r => r.referrer_id)).size}</p>
              </div>
              <div className="border border-[#1A1A1A]/10 p-5" data-testid="ref-recent">
                <p className="label-eyebrow">This Month</p>
                <p className="font-editorial text-4xl mt-2 text-[#C9A968]">
                  {filteredReferrals.filter(r => new Date(r.referral_date) >= new Date(Date.now()-30*86400000)).length}
                </p>
              </div>
            </div>

            <div className="flex justify-end mb-3">
              <SortToggle dir={sortReferrals.dir} onToggle={sortReferrals.toggle} testId="sort-referrals" />
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date of Referral</TableHead>
                  <TableHead>Referrer</TableHead>
                  <TableHead>Referrer Company</TableHead>
                  <TableHead>Referred Name</TableHead>
                  <TableHead>Mobile</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Comments</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReferrals.length ? applySort(filteredReferrals, sortReferrals.dir).map((r, i) => (
                  <TableRow key={r.id || i} data-testid={`referral-row-${i}`}>
                    <TableCell className="font-medium text-[#1E2C5A]">{r.referral_date}</TableCell>
                    <TableCell>
                      {r.referrer_name || "—"}<br/>
                      <span className="text-xs text-[#8A8A8A]">{r.referrer_mobile}</span>
                    </TableCell>
                    <TableCell className="text-xs">{r.referrer_company || "—"}</TableCell>
                    <TableCell>{r.referred_name}</TableCell>
                    <TableCell className="font-mono text-xs">{r.referred_mobile}</TableCell>
                    <TableCell className="text-xs">{r.referred_location}</TableCell>
                    <TableCell className="text-xs text-[#5C5C5C] italic max-w-xs truncate">{r.comments || "—"}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow><TableCell colSpan={7} className="text-center text-[#8A8A8A] py-6">{referralSearch ? "No referrals match your search." : "No referrals yet."}</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TabsContent>

          {/* TILE TYPES */}
          <TabsContent value="tile-types" className="mt-8">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
              <div>
                <p className="label-eyebrow">Manage Tile Types</p>
                <p className="text-xs text-[#8A8A8A] mt-1">These appear in the product creation dropdown. Examples: floor, wall, mosaic, border, skirting.</p>
              </div>
              <div className="flex gap-2 items-center">
                <ExportButton
                  rows={tileTypes.map(t => ({ name: t.name, created_at: t.created_at }))}
                  name="tile-types"
                  title="TileRoom Tile Types"
                  testId="export-tile-types"
                />
                <Input
                  className="rounded-none w-56"
                  placeholder="Add new type (e.g. mosaic)"
                  value={newTileTypeName}
                  onChange={(e) => setNewTileTypeName(e.target.value)}
                  data-testid="new-tile-type-name"
                />
                <Button
                  className="rounded-none bg-[#1E2C5A] hover:bg-[#141F45]"
                  onClick={async () => {
                    if (!newTileTypeName.trim()) return;
                    const created = await createTileType(newTileTypeName.trim());
                    if (created) setNewTileTypeName("");
                  }}
                  data-testid="add-tile-type-btn"
                >
                  <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} /> Add Type
                </Button>
              </div>
            </div>
            <div className="flex justify-end mb-3">
              <SortToggle dir={sortTileTypes.dir} onToggle={sortTileTypes.toggle} testId="sort-tile-types" />
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tileTypes.length ? applySort(tileTypes, sortTileTypes.dir).map(t => (
                  <TableRow key={t.id} data-testid={`admin-tile-type-${t.id}`}>
                    <TableCell>
                      <Input
                        className="rounded-none uppercase"
                        defaultValue={t.name}
                        onBlur={(e) => { if (e.target.value !== t.name) renameTileType(t.id, e.target.value); }}
                        data-testid={`edit-tile-type-${t.id}`}
                      />
                    </TableCell>
                    <TableCell className="text-xs text-[#8A8A8A]">{new Date(t.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <button onClick={() => deleteTileType(t.id)} className="text-red-700 inline-flex items-center gap-1 text-xs" data-testid={`del-tile-type-${t.id}`}>
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} /> Delete
                      </button>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow><TableCell colSpan={3} className="text-center text-[#8A8A8A] py-6">No tile types yet — add your first one above.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TabsContent>

          {/* VENDORS */}
          <TabsContent value="vendors" className="mt-8">
            <div className="flex justify-between mb-4 flex-wrap gap-2">
              <div className="flex gap-2">
                <ExportButton
                  rows={vendors.map(v => ({
                    company: v.company_name,
                    vendor: v.vendor_name,
                    gst_no: v.gst_no,
                    contact: v.contact_no,
                    email: v.email,
                    address: v.address,
                  }))}
                  name="vendors"
                  title="TileRoom Vendors"
                  testId="export-vendors"
                />
                <Button
                  variant="outline"
                  className="rounded-none border-[#1E2C5A] text-[#1E2C5A] hover:bg-[#1E2C5A] hover:text-white"
                  onClick={() => downloadTemplate("vendors")}
                  data-testid="download-vendor-template"
                >
                  <Download className="w-4 h-4 mr-1" strokeWidth={1.5} /> CSV Template
                </Button>
                <Button
                  variant="outline"
                  className="rounded-none border-[#C9A968] text-[#1E2C5A] hover:bg-[#C9A968]"
                  onClick={() => setUploadDlg({ open: true, kind: "vendors" })}
                  data-testid="bulk-upload-vendors"
                >
                  <Upload className="w-4 h-4 mr-1" strokeWidth={1.5} /> Bulk Upload
                </Button>
              </div>
              <Button
                className="rounded-none bg-[#1E2C5A] hover:bg-[#141F45]"
                onClick={() => setVendorDlg({ open: true, mode: "create", data: null })}
                data-testid="new-vendor-btn"
              >
                <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} /> New Vendor
              </Button>
            </div>
            <div className="flex justify-end mb-3">
              <SortToggle dir={sortVendors.dir} onToggle={sortVendors.toggle} testId="sort-vendors" />
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Vendor Name</TableHead>
                  <TableHead>GST No.</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vendors.length ? applySort(vendors, sortVendors.dir).map(v => (
                  <TableRow key={v.id} data-testid={`admin-vendor-${v.id}`}>
                    <TableCell className="font-medium">{v.company_name}</TableCell>
                    <TableCell>{v.vendor_name}</TableCell>
                    <TableCell className="text-xs font-mono">{v.gst_no}</TableCell>
                    <TableCell>{v.contact_no}</TableCell>
                    <TableCell className="text-xs">{v.email}</TableCell>
                    <TableCell className="text-xs max-w-xs truncate">{v.address}</TableCell>
                    <TableCell className="space-x-3 whitespace-nowrap">
                      <button onClick={() => setVendorDlg({ open: true, mode: "edit", data: v })} className="text-[#1E2C5A] hover:text-[#141F45] inline-flex items-center gap-1 text-xs" data-testid={`edit-vendor-${v.id}`}>
                        <Pencil className="w-4 h-4" strokeWidth={1.5} /> Edit
                      </button>
                      <button onClick={() => delVendor(v.id)} className="text-red-700 inline-flex items-center gap-1 text-xs" data-testid={`del-vendor-${v.id}`}>
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} /> Delete
                      </button>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow><TableCell colSpan={7} className="text-center text-[#8A8A8A] py-6">No vendors yet.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TabsContent>

          {/* BUYERS */}
          <TabsContent value="buyers" className="mt-8">
            <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
              <p className="label-eyebrow">Trade Portal Approvals</p>
              <div className="flex gap-2 flex-wrap items-center">
                <ExportButton
                  rows={buyers.map(b => ({
                    company: b.company_name,
                    person: b.person_name || b.name,
                    mobile: b.mobile || b.whatsapp,
                    location: b.location,
                    address: b.address,
                    gst_no: b.gst_no,
                    status: b.status,
                    access_level: b.access_level || "full",
                    signed_up: b.created_at,
                  }))}
                  name="buyers"
                  title="TileRoom Buyers"
                  testId="export-buyers"
                />
                <Button
                  className="rounded-none bg-[#1E2C5A] hover:bg-[#141F45]"
                  onClick={() => setAddBuyerDlg(true)}
                  data-testid="new-buyer-btn"
                >
                  <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} /> New Customer
                </Button>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap mb-6" data-testid="buyer-filter">
              {["pending", "approved", "rejected", "blocked", "all"].map(s => (
                <button
                  key={s}
                  onClick={() => setBuyerFilter(s)}
                  className={`px-4 py-2 text-xs uppercase tracking-[0.15em] border transition-colors ${
                    buyerFilter === s ? "bg-[#1E2C5A] text-white border-[#1E2C5A]" : "border-[#1A1A1A]/20 text-[#5C5C5C]"
                  }`}
                  data-testid={`buyer-filter-${s}`}
                >{s}</button>
              ))}
            </div>
            <div className="mb-6">
              <label className="label-eyebrow block mb-1">Location</label>
              <select
                className="h-10 border border-input rounded-none px-3 text-sm min-w-[220px] bg-transparent"
                value={buyerLocationFilter}
                onChange={(e) => setBuyerLocationFilter(e.target.value)}
                data-testid="buyer-location-filter"
              >
                <option value="all">All locations</option>
                {buyerLocations.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
              <SortToggle dir={sortBuyers.dir} onToggle={sortBuyers.toggle} testId="sort-buyers" />
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Person</TableHead>
                  <TableHead>Mobile</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>GST No.</TableHead>
                  <TableHead>Signed Up</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Access</TableHead>
                  <TableHead>Credits</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {buyers.filter(b => b.status !== "legacy" && b.password_hash === undefined ? false : true).length === 0 && (
                  <TableRow><TableCell colSpan={10} className="text-center text-[#8A8A8A] py-6">No buyers in this filter.</TableCell></TableRow>
                )}
                {applySort(buyers, sortBuyers.dir).map(b => (
                  <TableRow key={b.id} data-testid={`admin-buyer-${b.id}`}>
                    <TableCell className="font-medium">{b.company_name || "—"}</TableCell>
                    <TableCell>{b.person_name || b.name}</TableCell>
                    <TableCell className="font-mono text-xs">{b.mobile || b.whatsapp}</TableCell>
                    <TableCell className="text-xs">{b.location}</TableCell>
                    <TableCell className="text-xs">{b.gst_no || "—"}</TableCell>
                    <TableCell className="text-xs">{new Date(b.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <span className={
                        b.status === "pending" ? "text-[#C9A968] uppercase text-xs tracking-wider" :
                        b.status === "approved" ? "text-green-700 uppercase text-xs tracking-wider" :
                        b.status === "rejected" ? "text-red-700 uppercase text-xs tracking-wider" :
                        "text-[#8A8A8A] uppercase text-xs tracking-wider"
                      }>{b.status}</span>
                    </TableCell>
                    <TableCell>
                      {b.status === "approved" ? (
                        <select
                          className="h-8 border border-input rounded-none px-2 text-xs bg-transparent"
                          value={b.access_level || "full"}
                          onChange={(e) => setBuyerAccess(b.id, e.target.value)}
                          data-testid={`access-select-${b.id}`}
                        >
                          <option value="full">FULL</option>
                          <option value="limited">LIMITED</option>
                        </select>
                      ) : (
                        <span className="text-xs text-[#8A8A8A]">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {b.status === "approved" ? (
                        <button
                          onClick={() => setCreditsDlg({ open: true, buyer: b })}
                          className="text-xs text-[#C9A968] hover:underline"
                          data-testid={`credits-btn-${b.id}`}
                        >₹{Number(b.credits || 0).toLocaleString("en-IN")}</button>
                      ) : (
                        <span className="text-xs text-[#8A8A8A]">—</span>
                      )}
                    </TableCell>
                    <TableCell className="space-x-3 whitespace-nowrap">
                      {b.status === "pending" && (
                        <>
                          <button onClick={() => approveBuyer(b.id)} className="text-green-700 inline-flex items-center gap-1 text-xs" data-testid={`approve-buyer-${b.id}`}>
                            <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} /> Approve
                          </button>
                          <button onClick={() => rejectBuyer(b.id)} className="text-red-700 inline-flex items-center gap-1 text-xs" data-testid={`reject-buyer-${b.id}`}>
                            <XCircle className="w-4 h-4" strokeWidth={1.5} /> Reject
                          </button>
                        </>
                      )}
                      {b.status === "approved" && (
                        <button onClick={async () => { if(window.confirm("Block this user? They cannot login again.")) { await api.patch(`/admin/buyers/${b.id}/block`); toast.success("Blocked"); const params = buyerFilter === "all" ? {} : { status: buyerFilter }; api.get("/admin/buyers", { params }).then(r => setBuyers(r.data)); } }} className="text-red-700 inline-flex items-center gap-1 text-xs" data-testid={`block-buyer-${b.id}`}>
                          <XCircle className="w-4 h-4" strokeWidth={1.5} /> Block
                        </button>
                      )}
                      {b.status === "blocked" && (
                        <button onClick={async () => { await api.patch(`/admin/buyers/${b.id}/unblock`); toast.success("Unblocked"); const params = buyerFilter === "all" ? {} : { status: buyerFilter }; api.get("/admin/buyers", { params }).then(r => setBuyers(r.data)); }} className="text-green-700 inline-flex items-center gap-1 text-xs" data-testid={`unblock-buyer-${b.id}`}>
                          <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} /> Unblock
                        </button>
                      )}
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Permanently delete buyer "${b.company_name || b.mobile}"?\n\nThis also removes all their orders, projects, credits, returns, referrals — this action cannot be undone.`)) return;
                          try {
                            await api.delete(`/admin/buyers/${b.id}`);
                            toast.success("Buyer and all associated data deleted");
                            const params = {};
                            if (buyerFilter !== "all") params.status = buyerFilter;
                            if (buyerLocationFilter && buyerLocationFilter !== "all") params.location = buyerLocationFilter;
                            api.get("/admin/buyers", { params }).then(r => setBuyers(r.data));
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "Delete failed");
                          }
                        }}
                        className="text-red-700 hover:text-red-900 inline-flex items-center gap-1 text-xs"
                        data-testid={`delete-buyer-${b.id}`}
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} /> Delete
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TabsContent>

          {/* LAYERS */}
          <TabsContent value="layers" className="mt-8">
            <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
              <p className="label-eyebrow">Tile Layer Profiles</p>
              <div className="flex gap-2 items-center flex-wrap">
                <Input
                  className="rounded-none w-64"
                  placeholder="Search any attribute..."
                  value={layerSearch}
                  onChange={(e) => setLayerSearch(e.target.value)}
                  data-testid="layer-search"
                />
                <ExportButton
                  rows={filteredLayers.map(l => ({
                    name: l.name,
                    contact: l.contact,
                    address: l.address,
                    experience_years: l.experience_years,
                    rating: l.rating,
                    bio: l.bio,
                  }))}
                  name="tile-layers"
                  title="TileRoom Tile Layers"
                  testId="export-layers"
                />
                <Button
                  className="rounded-none bg-[#1E2C5A] hover:bg-[#141F45]"
                  onClick={() => setLayerDlg({ open: true, mode: "create", data: null })}
                  data-testid="new-layer-btn"
                >
                  <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} /> New Tile Layer
                </Button>
              </div>
            </div>

            <div className="flex justify-end mb-3">
              <SortToggle dir={sortLayers.dir} onToggle={sortLayers.toggle} testId="sort-layers" />
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead>Experience</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLayers.length ? applySort(filteredLayers, sortLayers.dir).map(l => (
                  <TableRow key={l.id} data-testid={`admin-layer-${l.id}`}>
                    <TableCell className="font-medium">{l.name}</TableCell>
                    <TableCell>{l.contact}</TableCell>
                    <TableCell>{l.address}</TableCell>
                    <TableCell>{l.experience_years} yrs</TableCell>
                    <TableCell>{l.rating}</TableCell>
                    <TableCell className="space-x-3 whitespace-nowrap">
                      <button
                        onClick={() => setLayerDlg({ open: true, mode: "edit", data: l })}
                        className="text-[#1E2C5A] hover:text-[#141F45] inline-flex items-center gap-1 text-xs"
                        data-testid={`edit-layer-${l.id}`}
                      >
                        <Pencil className="w-4 h-4" strokeWidth={1.5} /> Edit
                      </button>
                      <button
                        onClick={() => delLayer(l.id)}
                        className="text-red-700 hover:text-red-900 inline-flex items-center gap-1 text-xs"
                        data-testid={`del-layer-${l.id}`}
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} /> Delete
                      </button>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow><TableCell colSpan={6} className="text-center text-[#8A8A8A] py-6">{layerSearch ? "No tile layers match your search." : "No tile layers yet."}</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TabsContent>

          <TabsContent value="quotes" className="mt-8">
            <HeroQuotesPanel />
          </TabsContent>

          <TabsContent value="to-arrange" className="mt-8">
            <BackordersPanel />
          </TabsContent>

          <TabsContent value="pending-deliveries" className="mt-8">
            <PendingDeliveriesPanel />
          </TabsContent>

          {hasPerm(me, "mod.appointments") && (
            <TabsContent value="appointments" className="mt-8">
              <AppointmentsPanel />
            </TabsContent>
          )}

          {hasPerm(me, "mod.reports") && (
            <TabsContent value="reports" className="mt-8">
              <ReportsPanel me={me} />
            </TabsContent>
          )}

          {me.is_super && (
            <TabsContent value="users" className="mt-8">
              <UsersPanel />
            </TabsContent>
          )}
          {me.is_super && (
            <TabsContent value="employees" className="mt-8">
              <AdminEmployees />
            </TabsContent>
          )}
          {me.is_super && (
            <TabsContent value="announcements" className="mt-8">
              <AdminBroadcasts />
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Dialogs */}
      <ProductDialog
        open={productDlg.open}
        onOpenChange={(o) => setProductDlg(s => ({ ...s, open: o }))}
        initial={productDlg.data}
        mode={productDlg.mode}
        onSave={saveProduct}
        tileTypes={tileTypes}
        onCreateTileType={createTileType}
        productTiers={productTiers}
        onCreateProductTier={createProductTier}
      />
      <LayerDialog
        open={layerDlg.open}
        onOpenChange={(o) => setLayerDlg(s => ({ ...s, open: o }))}
        initial={layerDlg.data}
        mode={layerDlg.mode}
        onSave={saveLayer}
      />
      <VendorDialog
        open={vendorDlg.open}
        onOpenChange={(o) => setVendorDlg(s => ({ ...s, open: o }))}
        initial={vendorDlg.data}
        mode={vendorDlg.mode}
        onSave={saveVendor}
      />
      <UploadDialog
        open={uploadDlg.open}
        kind={uploadDlg.kind}
        onOpenChange={(o) => setUploadDlg(s => ({ ...s, open: o }))}
        onUpload={uploadCSV}
      />
      <AddBuyerDialog
        open={addBuyerDlg}
        onOpenChange={setAddBuyerDlg}
        onCreated={(created) => {
          if (created?._temp_password) {
            toast.success(`Customer created. Temp password: ${created._temp_password}`, { duration: 8000 });
          }
          const params = {};
          if (buyerFilter !== "all") params.status = buyerFilter;
          if (buyerLocationFilter && buyerLocationFilter !== "all") params.location = buyerLocationFilter;
          api.get("/admin/buyers", { params }).then(r => setBuyers(r.data));
          api.get("/admin/buyer-locations").then(r => setBuyerLocations(r.data)).catch(() => {});
        }}
      />
      <OrderDiscountDialog
        state={discountDlg}
        onClose={() => setDiscountDlg({ open: false, order: null })}
        onApplied={() => api.get("/orders").then(r => setOrders(r.data))}
      />
      <NewOrderDialog
        open={newOrderDlg}
        onClose={() => setNewOrderDlg(false)}
        onCreated={() => api.get("/orders").then(r => setOrders(r.data))}
      />
      <CreditsDialog
        state={creditsDlg}
        onClose={() => setCreditsDlg({ open: false, buyer: null })}
        onUpdated={() => {
          const params = {};
          if (buyerFilter !== "all") params.status = buyerFilter;
          if (buyerLocationFilter && buyerLocationFilter !== "all") params.location = buyerLocationFilter;
          api.get("/admin/buyers", { params }).then(r => setBuyers(r.data));
        }}
      />
      <EditOrderDialog
        state={editOrderDlg}
        onClose={() => setEditOrderDlg({ open: false, order: null })}
        onSaved={() => api.get("/orders").then(r => setOrders(r.data))}
      />
      <DispatchInfoDialog
        state={dispatchDlg}
        onClose={() => setDispatchDlg({ open: false, order: null })}
        onSaved={() => api.get("/orders").then(r => setOrders(r.data))}
      />
      <DeliveryPhotosDialog
        state={deliveryPhotosDlg}
        onClose={() => setDeliveryPhotosDlg({ open: false, order: null })}
        onSaved={() => api.get("/orders").then(r => setOrders(r.data))}
      />
      <OtherCostDialog
        state={otherCostDlg}
        onClose={() => setOtherCostDlg({ open: false, order: null })}
        onSaved={() => api.get("/orders").then(r => setOrders(r.data))}
      />
    </div>
  );
}


// ============ Other Cost Dialog ============
function OtherCostDialog({ state, onClose, onSaved }) {
  const [amount, setAmount] = useState(0);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (state.open && state.order) {
      setAmount(state.order.other_cost || 0);
      setReason(state.order.other_cost_reason || "");
    }
  }, [state.open, state.order]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/orders/${state.order.id}/other-cost`, { amount: Number(amount) || 0, reason });
      toast.success(Number(amount) > 0 ? `Other cost ₹${Number(amount).toFixed(2)} added` : "Other cost cleared");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const order = state.order || {};
  const previewTotal = (Number(order.total || 0) - Number(order.discount_amount || 0) + (Number(amount) || 0)).toFixed(2);
  return (
    <Dialog open={state.open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="other-cost-dialog">
        <DialogHeader>
          <DialogTitle className="font-editorial tracking-editorial text-2xl">
            Other Cost · Order #{order.order_no}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-[#5C5C5C]">
            Extra cost that will be <strong>added</strong> to the buyer's payable amount (freight top-up, handling, insurance, etc.). Enter 0 to remove.
          </p>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Amount (₹)</Label>
            <Input type="number" step="0.01" min="0" className="rounded-none" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="other-cost-amount" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Reason (visible to buyer)</Label>
            <Input className="rounded-none" placeholder="e.g. Freight top-up · Special crate" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="other-cost-reason" />
          </div>
          <p className="text-xs text-[#5C5C5C] bg-[#F0EFEA] border border-[#1A1A1A]/10 p-3" data-testid="other-cost-preview">
            New Grand Total (Payable): <strong className="text-[#1E2C5A]">₹{previewTotal}</strong>
          </p>
          <Button onClick={submit} disabled={busy} className="rounded-none bg-[#1E2C5A] hover:bg-[#141F45] w-full" data-testid="save-other-cost">
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ============ Delivery Photos Dialog ============
function DeliveryPhotosDialog({ state, onClose, onSaved }) {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (state.open && state.order) {
      setPhotos(state.order.delivery_photos || []);
    }
  }, [state.open, state.order]);

  const upload = async (file) => {
    if (!file || !state.order) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/orders/${state.order.id}/delivery-photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotos(data.delivery_photos || []);
      toast.success("Photo uploaded");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (url) => {
    if (!window.confirm("Remove this photo?")) return;
    try {
      await api.delete(`/admin/orders/${state.order.id}/delivery-photos`, { params: { url } });
      setPhotos(prev => prev.filter(p => p !== url));
      toast.success("Photo removed");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Dialog open={state.open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="delivery-photos-dialog">
        <DialogHeader>
          <DialogTitle className="font-editorial tracking-editorial text-2xl">
            Delivery Photos · Order #{state.order?.order_no}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-[#5C5C5C]">
            Upload photos taken at the time of delivery — cartons, unloading, buyer sign-off, or any evidence you'd
            like on file. Photos are stored against this order and visible to admin only.
          </p>
          <label className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.15em] cursor-pointer transition-colors ${uploading ? "bg-[#8A8A8A] text-white" : "bg-[#1E2C5A] text-white hover:bg-[#141F45]"}`} data-testid="delivery-photo-upload-label">
            <Upload className="w-3.5 h-3.5" strokeWidth={1.5} />
            {uploading ? "Uploading..." : "Choose photo"}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              disabled={uploading}
              onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }}
              data-testid="delivery-photo-input"
            />
          </label>

          {photos.length === 0 ? (
            <div className="border border-dashed border-[#1A1A1A]/15 p-10 text-center text-sm text-[#8A8A8A]" data-testid="no-delivery-photos">
              No photos uploaded yet.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="delivery-photos-grid">
              {photos.map((url, i) => (
                <div key={url || `dp-${i}`} className="relative group border border-[#1A1A1A]/10 bg-[#F5F4EF]">
                  <a href={toAbs(url)} target="_blank" rel="noreferrer">
                    <img src={toAbs(url)} alt={`Delivery photo ${i + 1}`} className="w-full h-32 object-cover" data-testid={`delivery-photo-${i}`} />
                  </a>
                  <button
                    onClick={() => remove(url)}
                    className="absolute top-1 right-1 bg-red-700 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`delete-delivery-photo-${i}`}
                    aria-label="Delete photo"
                  >
                    <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ============ Dispatch Info Dialog ============
function DispatchInfoDialog({ state, onClose, onSaved }) {
  const [vehicleNo, setVehicleNo] = useState("");
  const [driverMobile, setDriverMobile] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (state.open && state.order) {
      setVehicleNo(state.order.vehicle_no || "");
      setDriverMobile(state.order.driver_mobile || "");
      setNotes(state.order.dispatch_notes || "");
    }
  }, [state.open, state.order]);

  const isValid = (vehicleNo || "").trim().length > 0 && (driverMobile || "").trim().length >= 6;

  const submit = async () => {
    if (!isValid) { toast.error("Vehicle No. and Driver Mobile are mandatory"); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/orders/${state.order.id}/dispatch-info`, {
        vehicle_no: vehicleNo.trim(),
        driver_mobile: driverMobile.trim(),
        dispatch_notes: notes.trim() || null,
      });
      toast.success("Dispatch info saved — Invoice / PO / Challan unlocked");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  if (!state.open || !state.order) return null;
  const o = state.order;

  return (
    <Dialog open={state.open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg rounded-none" data-testid="dispatch-info-dialog">
        <DialogHeader>
          <DialogTitle className="font-editorial text-2xl flex items-center gap-2">
            <Truck className="w-5 h-5 text-[#1E2C5A]" strokeWidth={1.5} />
            Dispatch Info
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="border border-[#1A1A1A]/10 bg-[#FAFAFA] p-3 text-xs">
            <p className="text-[#5C5C5C]">
              Order <span className="font-mono font-medium text-[#1A1A1A]">{o.order_no}</span>
              <span className="mx-2 text-[#8A8A8A]">·</span>
              {o.customer_name}
            </p>
            <p className="text-[#8A8A8A] mt-1">
              Fill these once inventory is ready. Both fields are mandatory — Invoice, PO and Delivery Challan downloads will unlock only after saving.
            </p>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-[#5C5C5C]">
              Vehicle No. <span className="text-red-700">*</span>
            </Label>
            <Input
              className="rounded-none mt-1 font-mono tracking-wider uppercase"
              placeholder="MH 04 AB 1234"
              value={vehicleNo}
              onChange={(e) => setVehicleNo(e.target.value.toUpperCase())}
              data-testid="dispatch-vehicle-no"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#5C5C5C]">
              Driver Mobile <span className="text-red-700">*</span>
            </Label>
            <Input
              type="tel"
              className="rounded-none mt-1"
              placeholder="10-digit mobile"
              value={driverMobile}
              onChange={(e) => setDriverMobile(e.target.value)}
              data-testid="dispatch-driver-mobile"
            />
            <p className="text-[10px] text-[#8A8A8A] mt-1">Kept internal — this does NOT appear on the customer-facing invoice.</p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#5C5C5C]">Notes <span className="normal-case tracking-normal text-[#8A8A8A]">— optional</span></Label>
            <textarea
              rows={3}
              className="w-full border border-[#1A1A1A]/20 p-3 text-sm mt-1 focus:outline-none focus:border-[#1E2C5A] resize-none"
              placeholder="e.g. Transporter: XYZ Logistics · LR#12345 · Dispatched from Bhiwandi warehouse"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="dispatch-notes"
            />
          </div>

          <div className="border-t border-[#1A1A1A]/10 pt-3 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="text-xs uppercase tracking-[0.2em] text-[#5C5C5C] hover:text-[#1A1A1A]"
              data-testid="dispatch-cancel"
            >
              Cancel
            </button>
            <Button
              onClick={submit}
              disabled={busy || !isValid}
              className="rounded-none bg-[#1E2C5A] hover:bg-[#141F45]"
              data-testid="dispatch-save"
            >
              {busy ? "Saving…" : "Save & Unlock Downloads"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============ Order Discount Dialog ============
function OrderDiscountDialog({ state, onClose, onApplied }) {
  const [kind, setKind] = useState("percent");
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (state.open && state.order) {
      setKind(state.order.additional_discount_type || "percent");
      setValue(state.order.additional_discount_value ? String(state.order.additional_discount_value) : "");
      setReason(state.order.additional_discount_reason || "");
    }
  }, [state]);
  if (!state.open || !state.order) return null;
  const total = state.order.total || 0;
  const v = parseFloat(value) || 0;
  const previewDiscount = kind === "percent" ? Math.min(total * (v / 100), total) : Math.min(v, total);
  const previewGrand = Math.max(0, total - previewDiscount);
  const submit = async () => {
    if (v < 0) { toast.error("Value must be non-negative"); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/orders/${state.order.id}/discount`, { kind, value: v, reason });
      toast.success("Discount applied");
      onApplied();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={state.open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-md" data-testid="discount-dialog">
        <DialogHeader>
          <DialogTitle className="font-editorial tracking-editorial text-2xl">Additional Special Discount</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-[#8A8A8A]">Order {state.order.order_no} · Total ₹{total.toFixed(2)}</p>
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div>
            <Label className="label-eyebrow">Type</Label>
            <select className="w-full h-10 border border-input rounded-none px-3 text-sm bg-transparent" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="discount-kind">
              <option value="percent">Percent (%)</option>
              <option value="amount">Amount (₹)</option>
            </select>
          </div>
          <div>
            <Label className="label-eyebrow">Value</Label>
            <Input type="number" className="rounded-none" value={value} onChange={(e) => setValue(e.target.value)} placeholder="0" data-testid="discount-value" />
          </div>
          <div className="col-span-2">
            <Label className="label-eyebrow">Reason (optional)</Label>
            <Input className="rounded-none" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Bulk order · Loyalty" data-testid="discount-reason" />
          </div>
        </div>
        <div className="border-t border-[#1A1A1A]/10 mt-6 pt-4">
          <div className="flex justify-between text-sm"><span>Total</span><span>₹{total.toFixed(2)}</span></div>
          <div className="flex justify-between text-sm text-red-700"><span>Discount</span><span>− ₹{previewDiscount.toFixed(2)}</span></div>
          <div className="flex justify-between font-editorial text-2xl text-[#1E2C5A] mt-2" data-testid="discount-preview-grand"><span>Grand Total</span><span>₹{previewGrand.toFixed(2)}</span></div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-wider text-[#5C5C5C]">Cancel</button>
          <Button onClick={submit} disabled={busy} className="rounded-none bg-[#1E2C5A]" data-testid="discount-apply">Apply</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============ New Order (on-behalf) Dialog ============
function NewOrderDialog({ open, onClose, onCreated }) {
  const [buyers, setBuyers] = useState([]);
  const [products, setProducts] = useState([]);
  const [buyerId, setBuyerId] = useState("");
  const [rows, setRows] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      api.get("/admin/buyers?status=approved").then(r => setBuyers(r.data));
      api.get("/products").then(r => setProducts(r.data));
      setBuyerId(""); setRows([{ _key: _rowKey(), product_id: "", boxes: 1, length: 10, width_or_height: 10 }]); setNote("");
    }
  }, [open]);
  const updateRow = (i, field, val) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
  const addRow = () => setRows(rs => [...rs, { product_id: "", boxes: 1, length: 10, width_or_height: 10 }]);
  const removeRow = (i) => setRows(rs => rs.filter((_, idx) => idx !== i));
  const total = rows.reduce((sum, r) => {
    const p = products.find(x => x.id === r.product_id);
    if (!p) return sum;
    return sum + (Number(p.offer_price) * Number(p.tile_area_sqft || 1) * Number(p.packing || 1) * Number(r.boxes || 0));
  }, 0);
  const submit = async () => {
    if (!buyerId) { toast.error("Choose a buyer"); return; }
    const items = rows.filter(r => r.product_id && r.boxes > 0);
    if (!items.length) { toast.error("Add at least one item"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/orders", { buyer_id: buyerId, items, note });
      toast.success(`Order ${data.order_no} created (₹${data.total})`);
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="new-order-dialog">
        <DialogHeader>
          <DialogTitle className="font-editorial text-2xl">New Order (on-behalf of buyer)</DialogTitle>
        </DialogHeader>
        <div className="mb-4">
          <Label className="label-eyebrow">Buyer</Label>
          <select className="w-full h-10 border border-input rounded-none px-3 text-sm bg-transparent" value={buyerId} onChange={(e) => setBuyerId(e.target.value)} data-testid="new-order-buyer">
            <option value="">Choose a buyer...</option>
            {buyers.map(b => <option key={b.id} value={b.id}>{b.company_name} · {b.mobile}</option>)}
          </select>
        </div>
        <Label className="label-eyebrow">Items</Label>
        <div className="space-y-2 mb-4">
          <div className="grid grid-cols-12 gap-2 items-center px-2 text-[10px] uppercase tracking-[0.15em] text-[#8A8A8A]" data-testid="new-order-row-header">
            <span className="col-span-5">Product</span>
            <span className="col-span-2">Length (ft)</span>
            <span className="col-span-2">Width / Height (ft)</span>
            <span className="col-span-2">Quantity (Boxes)</span>
            <span className="col-span-1 text-right">×</span>
          </div>
          {rows.map((r, i) => {
            const p = products.find(x => x.id === r.product_id);
            const rate = p ? Number(p.offer_price || 0) : 0;
            const rowSubtotal = p ? rate * (p.tile_area_sqft || 1) * (p.packing || 1) * (Number(r.boxes) || 0) : 0;
            return (
              <div key={r._key || `row-${i}`} className="grid grid-cols-12 gap-2 items-center border border-[#1A1A1A]/10 p-2" data-testid={`order-row-${i}`}>
                <select className="col-span-5 h-9 border border-input rounded-none px-2 text-xs bg-transparent" value={r.product_id} onChange={(e) => updateRow(i, "product_id", e.target.value)}>
                  <option value="">Choose product...</option>
                  {products.map(pp => <option key={pp.id} value={pp.id}>{pp.our_code} · {pp.size} · ₹{pp.offer_price}</option>)}
                </select>
                <Input type="number" className="col-span-2 rounded-none h-9" placeholder="Length" value={r.length} onChange={(e) => updateRow(i, "length", parseFloat(e.target.value) || 0)} />
                <Input type="number" className="col-span-2 rounded-none h-9" placeholder="W/H" value={r.width_or_height} onChange={(e) => updateRow(i, "width_or_height", parseFloat(e.target.value) || 0)} />
                <Input type="number" className="col-span-2 rounded-none h-9" placeholder="Boxes" value={r.boxes} onChange={(e) => updateRow(i, "boxes", parseInt(e.target.value) || 0)} />
                <button onClick={() => removeRow(i)} className="col-span-1 text-red-700 text-xs">×</button>
                {p && (
                  <p className="col-span-12 text-[10px] text-[#8A8A8A]" data-testid={`row-rate-${i}`}>
                    Rate: <span className="text-[#1E2C5A]">₹{rate.toFixed(2)} / {(p.primary_uom || "BOX").toLowerCase()}</span>
                    &nbsp;·&nbsp; Row subtotal (GST-incl): <span className="text-[#1E2C5A]">≈ ₹{rowSubtotal.toFixed(0)}</span>
                  </p>
                )}
              </div>
            );
          })}
          <button onClick={addRow} className="text-xs text-[#1E2C5A] hover:underline" data-testid="new-order-add-row">+ Add another item</button>
        </div>
        <div>
          <Label className="label-eyebrow">Note (optional)</Label>
          <Input className="rounded-none" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Called to place order" />
        </div>
        <div className="flex justify-between items-center mt-4 pt-4 border-t border-[#1A1A1A]/10">
          <p className="font-editorial text-2xl">Approx Total: ₹{total.toFixed(0)}</p>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-xs uppercase text-[#5C5C5C]">Cancel</button>
            <Button onClick={submit} disabled={busy} className="rounded-none bg-[#1E2C5A]" data-testid="new-order-submit">{busy ? "Creating..." : "Create Order"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============ Credits Dialog ============
function CreditsDialog({ state, onClose, onUpdated }) {
  const [data, setData] = useState(null);
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (state.open && state.buyer) {
      api.get(`/admin/buyers/${state.buyer.id}/credits`).then(r => setData(r.data));
      setDelta(""); setReason("");
    }
  }, [state]);
  if (!state.open || !state.buyer) return null;
  const submit = async () => {
    const d = parseFloat(delta);
    if (isNaN(d) || d === 0) { toast.error("Enter a non-zero amount"); return; }
    setBusy(true);
    try {
      const { data: res } = await api.post(`/admin/buyers/${state.buyer.id}/credits`, { delta: d, reason });
      toast.success(`New balance: ₹${res.balance}`);
      setDelta(""); setReason("");
      const { data: refreshed } = await api.get(`/admin/buyers/${state.buyer.id}/credits`);
      setData(refreshed);
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={state.open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-lg max-h-[85vh] overflow-y-auto" data-testid="credits-dialog">
        <DialogHeader>
          <DialogTitle className="font-editorial text-2xl">Credits · {state.buyer.company_name}</DialogTitle>
        </DialogHeader>
        <div className="border border-[#1A1A1A]/10 p-6 mt-2 bg-[#F7F7F8]">
          <p className="label-eyebrow">Current Balance</p>
          <p className="font-editorial text-5xl mt-2 text-[#1E2C5A]" data-testid="admin-credits-balance">₹{Number(data?.balance || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
        </div>
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div>
            <Label className="label-eyebrow">Adjust (+/-)</Label>
            <Input type="number" className="rounded-none" value={delta} onChange={(e) => setDelta(e.target.value)} placeholder="e.g. 1000 or -500" data-testid="credits-delta" />
          </div>
          <div>
            <Label className="label-eyebrow">Reason</Label>
            <Input className="rounded-none" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Refund · Loyalty" data-testid="credits-reason" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 text-xs uppercase text-[#5C5C5C]">Close</button>
          <Button onClick={submit} disabled={busy} className="rounded-none bg-[#1E2C5A]" data-testid="credits-apply">Apply</Button>
        </div>
        <div className="mt-6">
          <p className="label-eyebrow mb-2">History</p>
          {(data?.history || []).length === 0 ? (
            <p className="text-xs text-[#8A8A8A]">No adjustments yet.</p>
          ) : (
            <ul className="divide-y divide-[#1A1A1A]/10 max-h-64 overflow-y-auto">
              {data.history.map((t, i) => (
                <li key={t.id || i} className="py-2 text-xs flex justify-between">
                  <span>
                    <span className={t.delta >= 0 ? "text-green-700 font-medium" : "text-red-700 font-medium"}>{t.delta >= 0 ? "+" : ""}₹{t.delta}</span>
                    <span className="text-[#8A8A8A]"> · bal ₹{t.balance_after}</span>
                    {t.reason && <span className="text-[#5C5C5C] italic"> — {t.reason}</span>}
                  </span>
                  <span className="text-[#8A8A8A]">{new Date(t.created_at).toLocaleDateString()}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AddBuyerDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({
    company_name: "", person_name: "", mobile: "", address: "",
    location: "", gst_no: "", access_level: "full", password: "",
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm({ company_name: "", person_name: "", mobile: "", address: "", location: "", gst_no: "", access_level: "full", password: "" });
  }, [open]);
  const submit = async () => {
    for (const k of ["company_name", "mobile", "gst_no"]) {
      if (!form[k]) { toast.error(`Please fill ${k.replace(/_/g, " ")}`); return; }
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/buyers", form);
      toast.success(`${data.company_name} added and approved`);
      onCreated(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-lg" data-testid="add-buyer-dialog">
        <DialogHeader>
          <DialogTitle className="font-editorial tracking-editorial text-2xl">New Customer</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div className="md:col-span-2">
            <Label className="label-eyebrow">Company / Firm Name *</Label>
            <Input className="rounded-none" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Acme Ceramics" data-testid="add-buyer-company" />
          </div>
          <div>
            <Label className="label-eyebrow">Person Name</Label>
            <Input className="rounded-none" value={form.person_name} onChange={(e) => setForm({ ...form, person_name: e.target.value })} placeholder="Contact person" data-testid="add-buyer-person" />
          </div>
          <div>
            <Label className="label-eyebrow">Mobile *</Label>
            <Input className="rounded-none" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="+91 98xxxxxxxx" data-testid="add-buyer-mobile" />
          </div>
          <div>
            <Label className="label-eyebrow">GST No. *</Label>
            <Input className="rounded-none" value={form.gst_no} onChange={(e) => setForm({ ...form, gst_no: e.target.value })} placeholder="27ABCDE1234F1Z5" data-testid="add-buyer-gst" />
          </div>
          <div>
            <Label className="label-eyebrow">Location</Label>
            <Input className="rounded-none" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="City, State" data-testid="add-buyer-location" />
          </div>
          <div className="md:col-span-2">
            <Label className="label-eyebrow">Address</Label>
            <Input className="rounded-none" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Street, City" data-testid="add-buyer-address" />
          </div>
          <div>
            <Label className="label-eyebrow">Access Level</Label>
            <select
              className="w-full h-10 border border-input rounded-none px-3 text-sm bg-transparent"
              value={form.access_level}
              onChange={(e) => setForm({ ...form, access_level: e.target.value })}
              data-testid="add-buyer-access"
            >
              <option value="full">FULL</option>
              <option value="limited">LIMITED (only &lt; ₹100)</option>
            </select>
          </div>
          <div>
            <Label className="label-eyebrow">Temp Password</Label>
            <Input className="rounded-none" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Auto: welcome@2026" data-testid="add-buyer-password" />
          </div>
          <div className="md:col-span-2 flex justify-end gap-2 mt-4">
            <button type="button" onClick={() => onOpenChange(false)} className="px-4 py-2 text-xs uppercase tracking-wider text-[#5C5C5C]">Cancel</button>
            <Button
              onClick={submit}
              disabled={busy}
              className="rounded-none bg-[#1E2C5A] hover:bg-[#141F45]"
              data-testid="add-buyer-submit"
            >
              {busy ? "Creating..." : "Create & Approve"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ============ Stock Check Card (Admin async availability check) ============
function StockCheckCard({ order, onReplied }) {
  const [replies, setReplies] = useState(() =>
    order.items.map(it => ({
      product_id: it.product_id,
      availability: it.availability === "unavailable" ? "unavailable" : "available",
      lead_time_days: it.lead_time_days || 0,
      remark: it.admin_remark || "",
    }))
  );
  const [note, setNote] = useState(order.stock_check_note || "");
  const [busy, setBusy] = useState(false);

  const update = (idx, field, val) =>
    setReplies(rs => rs.map((r, i) => i === idx ? { ...r, [field]: val } : r));

  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/orders/${order.id}/stock-reply`, { items: replies, note });
      toast.success(`Replied to ${order.customer_name} · ${order.order_no}`);
      onReplied();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reply");
    } finally { setBusy(false); }
  };

  const availableCount = replies.filter(r => r.availability === "available").length;
  const unavailableCount = replies.length - availableCount;

  return (
    <div className="border border-[#1A1A1A]/15 bg-white" data-testid={`stock-check-${order.order_no}`}>
      <div className="flex items-center justify-between px-6 py-4 bg-[#F0EFEA] border-b border-[#1A1A1A]/10">
        <div>
          <p className="label-eyebrow">Order #{order.order_no}</p>
          <p className="font-medium mt-1">{order.customer_name} · <span className="text-[#5C5C5C] text-sm">{order.customer_whatsapp}</span></p>
          <p className="text-xs text-[#8A8A8A] mt-1">Submitted {new Date(order.created_at).toLocaleString()}</p>
        </div>
        <div className="text-right text-xs">
          <p className="text-green-700 font-medium">{availableCount} available</p>
          {unavailableCount > 0 && <p className="text-[#8B7A3E] font-medium">{unavailableCount} unavailable</p>}
          <p className="text-[#8A8A8A] mt-1">Total ₹{order.total.toFixed(0)}</p>
        </div>
      </div>

      <div className="divide-y divide-[#1A1A1A]/10">
        {order.items.map((it, idx) => {
          const r = replies[idx];
          const unavailable = r.availability === "unavailable";
          return (
            <div key={it.product_id} className="grid grid-cols-12 gap-4 items-center px-6 py-4" data-testid={`sc-item-${it.our_code}`}>
              <div className="col-span-5">
                <p className="font-medium text-sm">{it.name}</p>
                <p className="text-xs text-[#8A8A8A]">{it.our_code} · {it.size} · {it.quantity_boxes} boxes</p>
              </div>
              <div className="col-span-4 flex items-center gap-4">
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={r.availability === "available"}
                    onChange={(e) => update(idx, "availability", e.target.checked ? "available" : "unavailable")}
                    className="accent-green-700 w-4 h-4"
                    data-testid={`sc-available-${it.our_code}`}
                  />
                  <span className={r.availability === "available" ? "text-green-700 font-medium" : "text-[#8A8A8A]"}>Available</span>
                </label>
              </div>
              <div className="col-span-3">
                {unavailable ? (
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      value={r.lead_time_days}
                      onChange={(e) => update(idx, "lead_time_days", parseInt(e.target.value) || 0)}
                      className="rounded-none h-8 w-20 text-sm"
                      data-testid={`sc-lead-${it.our_code}`}
                    />
                    <span className="text-xs text-[#8B7A3E]">days lead time</span>
                  </div>
                ) : (
                  <span className="text-xs text-[#8A8A8A]">—</span>
                )}
              </div>
              <div className="col-span-12">
                <label className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Remark for buyer (optional)</label>
                <textarea
                  rows={2}
                  value={r.remark || ""}
                  onChange={(e) => update(idx, "remark", e.target.value)}
                  placeholder="e.g. Slight shade variation from batch A · Reserved from Bhiwandi warehouse"
                  className="w-full text-xs border border-[#1A1A1A]/15 px-2 py-1.5 mt-1 focus:outline-none focus:border-[#1E2C5A]"
                  data-testid={`sc-remark-${it.our_code}`}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="px-6 py-4 border-t border-[#1A1A1A]/10 bg-[#FAF9F6] flex items-center justify-between gap-4">
        <Input
          className="rounded-none flex-1 max-w-lg"
          placeholder="Optional note to buyer…"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          data-testid={`sc-note-${order.order_no}`}
        />
        <Button
          onClick={submit}
          disabled={busy}
          className="rounded-none bg-[#1E2C5A] hover:bg-[#141F45]"
          data-testid={`sc-reply-${order.order_no}`}
        >
          {busy ? "Sending…" : "Reply to Buyer"}
        </Button>
      </div>
    </div>
  );
}

// ============ Edit Order Dialog ============
function EditOrderDialog({ state, onClose, onSaved }) {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (state.open && state.order) {
      api.get("/products").then(r => setProducts(r.data));
      setRows(state.order.items.map(it => ({
        _key: _rowKey(),
        product_id: it.product_id,
        boxes: it.quantity_boxes,
        length: it.length || 0,
        width_or_height: it.width_or_height || 0,
      })));
      setNote(state.order.admin_note || "");
    }
  }, [state]);

  if (!state.open || !state.order) return null;

  const updateRow = (i, field, val) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
  const addRow = () => setRows(rs => [...rs, { _key: _rowKey(), product_id: "", boxes: 1, length: 10, width_or_height: 10 }]);
  const removeRow = (i) => setRows(rs => rs.filter((_, idx) => idx !== i));

  const total = rows.reduce((sum, r) => {
    const p = products.find(x => x.id === r.product_id);
    if (!p) return sum;
    return sum + (Number(p.offer_price) * Number(p.tile_area_sqft || 1) * Number(p.packing || 1) * Number(r.boxes || 0));
  }, 0);

  const submit = async () => {
    const items = rows.filter(r => r.product_id && r.boxes > 0);
    if (!items.length) { toast.error("At least one item required"); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/orders/${state.order.id}`, { items, note });
      toast.success(`Order ${data.order?.order_no || ""} updated`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to edit order");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={state.open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="edit-order-dialog">
        <DialogHeader>
          <DialogTitle className="font-editorial text-2xl">Edit Order · {state.order.order_no}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-[#8A8A8A]">Modify items, quantities, or dimensions. Totals and any existing discount will be recalculated automatically.</p>
        <div className="space-y-2 mt-4">
          <div className="grid grid-cols-12 gap-2 items-center px-2 text-[10px] uppercase tracking-[0.15em] text-[#8A8A8A]" data-testid="edit-order-row-header">
            <span className="col-span-5">Product</span>
            <span className="col-span-2">Length (ft)</span>
            <span className="col-span-2">Width / Height (ft)</span>
            <span className="col-span-2">Quantity (Boxes)</span>
            <span className="col-span-1 text-right">×</span>
          </div>
          {rows.map((r, i) => {
            const p = products.find(x => x.id === r.product_id);
            const rate = p ? Number(p.offer_price || 0) : 0;
            const rowSubtotal = p ? rate * (p.tile_area_sqft || 1) * (p.packing || 1) * (Number(r.boxes) || 0) : 0;
            return (
              <div key={r._key || `edit-row-${i}`} className="grid grid-cols-12 gap-2 items-center border border-[#1A1A1A]/10 p-2" data-testid={`edit-row-${i}`}>
                <select className="col-span-5 h-9 border border-input rounded-none px-2 text-xs bg-transparent" value={r.product_id} onChange={(e) => updateRow(i, "product_id", e.target.value)}>
                  <option value="">Choose product...</option>
                  {products.map(pp => <option key={pp.id} value={pp.id}>{pp.our_code} · {pp.size} · ₹{pp.offer_price}</option>)}
                </select>
                <Input type="number" className="col-span-2 rounded-none h-9" placeholder="Length" value={r.length} onChange={(e) => updateRow(i, "length", parseFloat(e.target.value) || 0)} data-testid={`edit-length-${i}`} />
                <Input type="number" className="col-span-2 rounded-none h-9" placeholder="W/H" value={r.width_or_height} onChange={(e) => updateRow(i, "width_or_height", parseFloat(e.target.value) || 0)} data-testid={`edit-width-${i}`} />
                <Input type="number" className="col-span-2 rounded-none h-9" placeholder="Boxes" value={r.boxes} onChange={(e) => updateRow(i, "boxes", parseInt(e.target.value) || 0)} data-testid={`edit-boxes-${i}`} />
                <button onClick={() => removeRow(i)} className="col-span-1 text-red-700 text-xs" data-testid={`edit-remove-${i}`}>×</button>
                {p && (
                  <p className="col-span-12 text-[10px] text-[#8A8A8A]" data-testid={`edit-row-rate-${i}`}>
                    Rate: <span className="text-[#1E2C5A]">₹{rate.toFixed(2)} / {(p.primary_uom || "BOX").toLowerCase()}</span>
                    &nbsp;·&nbsp; Row subtotal (GST-incl): <span className="text-[#1E2C5A]">≈ ₹{rowSubtotal.toFixed(0)}</span>
                  </p>
                )}
              </div>
            );
          })}
          <button onClick={addRow} className="text-xs text-[#1E2C5A] hover:underline" data-testid="edit-add-row">+ Add another item</button>
        </div>
        <div className="mt-4">
          <Label className="label-eyebrow">Note (optional)</Label>
          <Input className="rounded-none" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason for edit" data-testid="edit-order-note" />
        </div>
        <div className="flex justify-between items-center mt-4 pt-4 border-t border-[#1A1A1A]/10">
          <p className="font-editorial text-2xl" data-testid="edit-order-total">New Subtotal: ₹{total.toFixed(0)}</p>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-xs uppercase text-[#5C5C5C]">Cancel</button>
            <Button onClick={submit} disabled={busy} className="rounded-none bg-[#1E2C5A]" data-testid="edit-order-save">{busy ? "Saving..." : "Save Changes"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============ Hero Quotes Panel (Landing rotator) ============
function HeroQuotesPanel() {
  const [quotes, setQuotes] = useState([]);
  const [text, setText] = useState("");
  const [author, setAuthor] = useState("TileRoom Studio");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/hero-quotes").then(r => setQuotes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    const t = text.trim();
    if (!t) { toast.error("Enter a quote first"); return; }
    setBusy(true);
    try {
      await api.post("/admin/hero-quotes", { text: t, author: author.trim() || "TileRoom Studio", is_active: true, sort_order: quotes.length });
      setText("");
      toast.success("Quote added");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    } finally { setBusy(false); }
  };

  const toggle = async (q) => {
    try {
      await api.patch(`/admin/hero-quotes/${q.id}`, { is_active: !q.is_active });
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const remove = async (q) => {
    if (!window.confirm(`Delete quote: "${q.text.substring(0, 60)}${q.text.length > 60 ? "…" : ""}"?`)) return;
    try {
      await api.delete(`/admin/hero-quotes/${q.id}`);
      toast.success("Quote removed");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const activeCount = quotes.filter(q => q.is_active).length;

  return (
    <div data-testid="hero-quotes-panel">
      <div className="mb-6">
        <p className="label-eyebrow">Landing Hero Rotator</p>
        <h3 className="font-editorial text-2xl mt-1">Manage quotes shown on the landing page</h3>
        <p className="text-xs text-[#8A8A8A] mt-2">Only <span className="font-medium text-[#1E2C5A]">active</span> quotes rotate on the landing hero. Inactive quotes are hidden but preserved. Sort order controls sequence.</p>
      </div>

      <div className="border border-[#1A1A1A]/15 p-6 bg-[#F0EFEA] mb-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
          <div className="lg:col-span-7">
            <Label className="label-eyebrow mb-1 block">Quote text</Label>
            <Input
              className="rounded-none"
              placeholder="e.g. A room begins with what you walk on."
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="new-quote-text"
            />
          </div>
          <div className="lg:col-span-3">
            <Label className="label-eyebrow mb-1 block">Author / Source</Label>
            <Input
              className="rounded-none"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              data-testid="new-quote-author"
            />
          </div>
          <div className="lg:col-span-2 flex items-end">
            <Button onClick={add} disabled={busy} className="rounded-none bg-[#1E2C5A] hover:bg-[#141F45] w-full" data-testid="add-quote-btn">
              <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} /> Add Quote
            </Button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-[#8A8A8A]" data-testid="quotes-count">{quotes.length} total · {activeCount} active on landing</p>
      </div>

      {quotes.length === 0 ? (
        <div className="border border-dashed border-[#1A1A1A]/15 p-16 text-center text-[#8A8A8A]" data-testid="no-quotes">
          No quotes yet. Add your first one above — it will start rotating on the landing hero.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">#</TableHead>
              <TableHead>Quote</TableHead>
              <TableHead className="w-56">Author</TableHead>
              <TableHead className="w-24">Active</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {quotes.map((q, i) => (
              <TableRow key={q.id} data-testid={`quote-row-${q.id}`}>
                <TableCell className="text-[#8A8A8A] text-xs">{String(i + 1).padStart(2, "0")}</TableCell>
                <TableCell className="font-editorial text-base leading-snug max-w-xl">&ldquo;{q.text}&rdquo;</TableCell>
                <TableCell className="text-xs text-[#5C5C5C]">{q.author}</TableCell>
                <TableCell>
                  <button
                    onClick={() => toggle(q)}
                    className={`text-xs uppercase tracking-[0.15em] px-2 py-1 border ${q.is_active ? "border-[#1E2C5A] text-[#1E2C5A] bg-white" : "border-[#8A8A8A] text-[#8A8A8A] bg-transparent"}`}
                    data-testid={`toggle-quote-${q.id}`}
                  >
                    {q.is_active ? "Active" : "Hidden"}
                  </button>
                </TableCell>
                <TableCell>
                  <button
                    onClick={() => remove(q)}
                    className="text-red-700 hover:text-red-900 inline-flex items-center gap-1 text-xs"
                    data-testid={`delete-quote-${q.id}`}
                  >
                    <Trash2 className="w-3 h-3" strokeWidth={1.5} /> Delete
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}


// ============ Backorders / To Arrange Goods Panel ============
// All statuses in the whole lifecycle (used for progress bar rendering)
const PROC_STAGES = [
  { key: "awaiting_procurement", label: "Awaiting Procurement" },
  { key: "ordered",              label: "Ordered" },
  { key: "picked",               label: "Picked" },
  { key: "shipped",              label: "Shipped" },
  { key: "inventory_ready",      label: "Inventory Ready" },
  { key: "ready_for_dispatch",   label: "Ready for Dispatch" },
  { key: "dispatched",           label: "Dispatched" },
  { key: "delivered",            label: "Delivered" },
];
// Statuses managed inside "To Arrange Goods"
const ARRANGE_STAGES = PROC_STAGES.slice(0, 4); // awaiting → ordered → picked → shipped
// Statuses managed inside "Pending Deliveries"
const DELIVERY_STAGES = PROC_STAGES.slice(4);   // inventory_ready → ready_for_dispatch → dispatched → delivered

function BackordersPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/backorders")
      .then(r => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="backorders-panel">
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="label-eyebrow">Post-Payment Procurement Queue</p>
          <h3 className="font-editorial text-2xl mt-1">To Arrange Goods</h3>
          <p className="text-xs text-[#8A8A8A] mt-2 max-w-2xl">
            Items paid for that need to be procured. Track each one through <em>Ordered → Picked → Shipped</em>.
            Once you tick <strong>Inventory Ready</strong>, the item moves to the <em>Pending Deliveries</em> tab and the buyer sees &quot;Goods are Ready&quot;.
          </p>
        </div>
        <button onClick={load} className="text-xs uppercase tracking-[0.2em] text-[#1E2C5A] hover:text-[#141F45]" data-testid="backorder-refresh">Refresh ↻</button>
      </div>

      {loading ? (
        <div className="border border-dashed border-[#1A1A1A]/15 p-16 text-center text-[#8A8A8A]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-[#1A1A1A]/15 p-16 text-center text-[#8A8A8A]" data-testid="no-backorders">
          Nothing to arrange right now. Items paid for that aren&apos;t yet in stock will land here.
        </div>
      ) : (
        <div className="space-y-6">
          {items.map(row => <ArrangeCard key={`${row.order_id}-${row.product_id}`} row={row} onSaved={load} />)}
        </div>
      )}
    </div>
  );
}

function ArrangeCard({ row, onSaved }) {
  const [status, setStatus] = useState(row.procurement_status);
  const [vendorNotes, setVendorNotes] = useState(row.vendor_notes || "");
  const [logisticsNotes, setLogisticsNotes] = useState(row.logistics_notes || "");
  const [transporterName, setTransporterName] = useState(row.transporter_name || "");
  const [busy, setBusy] = useState(false);

  const save = async (nextStatus, extra = {}) => {
    setBusy(true);
    try {
      const body = {
        procurement_status: nextStatus,
        vendor_notes: vendorNotes,
        logistics_notes: logisticsNotes,
        transporter_name: transporterName,
        ...extra,
      };
      await api.patch(`/admin/orders/${row.order_id}/items/${row.product_id}/procurement`, body);
      const label = PROC_STAGES.find(s => s.key === nextStatus)?.label;
      toast.success(`${row.our_code}: ${label}`);
      setStatus(nextStatus);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  const idx = PROC_STAGES.findIndex(s => s.key === status);

  return (
    <div className="border border-[#1A1A1A]/15 bg-white" data-testid={`backorder-row-${row.our_code}`}>
      {/* Header */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 p-5 border-b border-[#1A1A1A]/10">
        <div className="lg:col-span-4">
          <p className="label-eyebrow">Customer</p>
          <p className="font-medium mt-1">{row.customer_name || "—"}</p>
          <p className="text-xs text-[#5C5C5C]">{row.customer_whatsapp}</p>
          <p className="text-xs text-[#8A8A8A] mt-1">{row.customer_location}</p>
          <p className="text-xs text-[#8A8A8A] mt-3">
            Order <Link to={`/orders/${row.order_id}`} className="text-[#1E2C5A] hover:underline">#{row.order_no}</Link>
            {row.order_date && <> · {new Date(row.order_date).toLocaleDateString()}</>}
          </p>
          <p className="text-[10px] uppercase tracking-wide text-green-700 mt-1">Payment: {row.payment_status}</p>
        </div>
        <div className="lg:col-span-4">
          <p className="label-eyebrow">Product</p>
          <p className="font-medium mt-1">{row.product_name}</p>
          <p className="text-xs text-[#8A8A8A]">Code {row.our_code} · {row.size}</p>
          <p className="text-xs text-[#5C5C5C] mt-1">{row.quantity_boxes} boxes × {row.packing || "?"}/box</p>
          <p className="text-xs text-[#8B7A3E] mt-1">Lead time ~{row.lead_time_days || 0} days</p>
          <p className="text-sm mt-1">₹{(row.subtotal || 0).toFixed(0)}</p>
        </div>
        <div className="lg:col-span-4">
          <p className="label-eyebrow">Current stage</p>
          <p className="font-medium mt-1 uppercase tracking-wider text-[#8B7A3E]">{PROC_STAGES.find(s => s.key === status)?.label}</p>
          <Button
            onClick={() => save("inventory_ready")}
            disabled={busy || status === "inventory_ready"}
            className="rounded-none bg-[#C9A968] hover:bg-[#B69457] text-[#1E2C5A] w-full mt-3 text-xs uppercase tracking-[0.15em] disabled:opacity-40"
            data-testid={`backorder-ready-${row.our_code}`}
          >
            <CheckCircle2 className="w-4 h-4 mr-1" strokeWidth={1.5} /> Mark Inventory Ready
          </Button>
          <p className="text-[10px] text-[#8A8A8A] mt-2">Moves this item to <em>Pending Deliveries</em>.</p>
        </div>
      </div>

      {/* Stage-specific note fields */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border-b border-[#1A1A1A]/10">
        <StageBlock
          title="Ordered"
          eyebrow="Vendor details"
          placeholder="e.g. Vendor: Kajaria — 20 boxes — dispatch by 20 Jul. Contact: Raju 98xxx."
          value={vendorNotes}
          onChange={setVendorNotes}
          active={status === "ordered"}
          done={PROC_STAGES.findIndex(s => s.key === "ordered") < idx}
          onSave={() => save("ordered")}
          busy={busy}
          testId={`arrange-ordered-${row.our_code}`}
        />
        <StageBlock
          title="Picked"
          eyebrow="Logistics details"
          placeholder="e.g. Picked from Kajaria warehouse on 22 Jul. Handed to VRL Logistics via truck TN-38-AB-1234."
          value={logisticsNotes}
          onChange={setLogisticsNotes}
          active={status === "picked"}
          done={PROC_STAGES.findIndex(s => s.key === "picked") < idx}
          onSave={() => save("picked")}
          busy={busy}
          testId={`arrange-picked-${row.our_code}`}
        />
        <StageBlock
          title="Shipped"
          eyebrow="Transporter name"
          placeholder="e.g. VRL Logistics · Docket #VRL-BLR-99823 · Driver 9x-9x-9x"
          value={transporterName}
          onChange={setTransporterName}
          active={status === "shipped"}
          done={PROC_STAGES.findIndex(s => s.key === "shipped") < idx}
          onSave={() => save("shipped")}
          busy={busy}
          testId={`arrange-shipped-${row.our_code}`}
          last
        />
      </div>

      {/* Progress bar */}
      <div className="grid grid-cols-8 gap-1 px-5 py-3 bg-[#FAF9F6]">
        {PROC_STAGES.map((s, i) => (
          <div key={s.key} className={`border-t-2 pt-1.5 ${i <= idx ? "border-[#C9A968]" : "border-[#E5E5E5]"}`}>
            <p className={`text-[9px] uppercase tracking-wide ${i <= idx ? "text-[#8B7A3E] font-medium" : "text-[#8A8A8A]"}`}>{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function StageBlock({ title, eyebrow, placeholder, value, onChange, active, done, onSave, busy, testId, last }) {
  return (
    <div className={`p-5 ${last ? "" : "lg:border-r"} border-[#1A1A1A]/10 ${active ? "bg-[#FDF9EE]" : ""}`}>
      <div className="flex items-center gap-2 mb-1">
        {done ? <CheckCircle2 className="w-3.5 h-3.5 text-green-700" strokeWidth={1.5} /> : <span className={`inline-block w-2.5 h-2.5 rounded-full ${active ? "bg-[#C9A968]" : "bg-[#E5E5E5]"}`} />}
        <p className="label-eyebrow">{eyebrow}</p>
      </div>
      <p className="font-editorial text-lg mt-1 mb-3">{title}</p>
      <textarea
        rows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full border border-input rounded-none p-2 text-xs bg-transparent focus:border-[#1E2C5A] outline-none resize-none"
        data-testid={`${testId}-text`}
      />
      <Button
        onClick={onSave}
        disabled={busy}
        className="rounded-none bg-[#1E2C5A] hover:bg-[#141F45] w-full mt-2 text-xs uppercase tracking-[0.15em]"
        data-testid={`${testId}-save`}
      >
        Save & mark {title}
      </Button>
    </div>
  );
}

// ============ Pending Deliveries Panel (post-inventory-ready) ============
function PendingDeliveriesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/pending-deliveries")
      .then(r => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const advance = async (row, next) => {
    try {
      await api.patch(`/admin/orders/${row.order_id}/items/${row.product_id}/procurement`, { procurement_status: next });
      toast.success(`${row.our_code}: ${PROC_STAGES.find(s => s.key === next)?.label}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div data-testid="pending-deliveries-panel">
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="label-eyebrow">Inventory Ready → Delivered</p>
          <h3 className="font-editorial text-2xl mt-1">Pending Deliveries</h3>
          <p className="text-xs text-[#8A8A8A] mt-2 max-w-2xl">Backorder items that admin has marked <em>Inventory Ready</em>. Advance them through dispatch to delivery — buyer sees each update on <em>Yet to Receive</em>.</p>
        </div>
        <button onClick={load} className="text-xs uppercase tracking-[0.2em] text-[#1E2C5A] hover:text-[#141F45]" data-testid="pending-deliveries-refresh">Refresh ↻</button>
      </div>

      {loading ? (
        <div className="border border-dashed border-[#1A1A1A]/15 p-16 text-center text-[#8A8A8A]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-[#1A1A1A]/15 p-16 text-center text-[#8A8A8A]" data-testid="no-pending-deliveries">
          Nothing pending. Items you mark <em>Inventory Ready</em> under &quot;To Arrange Goods&quot; will appear here.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Customer</TableHead>
              <TableHead>Order</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Transporter</TableHead>
              <TableHead>Current stage</TableHead>
              <TableHead>Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map(row => {
              const idx = PROC_STAGES.findIndex(s => s.key === row.procurement_status);
              const nextStage = PROC_STAGES[idx + 1];
              return (
                <TableRow key={`${row.order_id}-${row.product_id}`} data-testid={`pending-row-${row.our_code}`}>
                  <TableCell>
                    <p className="font-medium">{row.customer_name}</p>
                    <p className="text-xs text-[#8A8A8A]">{row.customer_whatsapp}</p>
                  </TableCell>
                  <TableCell>
                    <Link to={`/orders/${row.order_id}`} className="text-[#1E2C5A] hover:underline text-xs">#{row.order_no}</Link>
                    <p className="text-xs text-[#8A8A8A]">{row.order_date ? new Date(row.order_date).toLocaleDateString() : ""}</p>
                  </TableCell>
                  <TableCell>
                    <p className="font-medium text-sm">{row.product_name}</p>
                    <p className="text-xs text-[#8A8A8A]">{row.our_code} · {row.size} · {row.quantity_boxes} boxes</p>
                  </TableCell>
                  <TableCell className="text-xs text-[#5C5C5C] max-w-xs">
                    {row.transporter_name || <span className="text-[#8A8A8A]">—</span>}
                  </TableCell>
                  <TableCell>
                    <span className="text-xs uppercase tracking-wider text-[#8B7A3E] font-medium">
                      {PROC_STAGES.find(s => s.key === row.procurement_status)?.label}
                    </span>
                  </TableCell>
                  <TableCell>
                    {nextStage && row.procurement_status !== "delivered" ? (
                      <Button
                        onClick={() => advance(row, nextStage.key)}
                        className="rounded-none bg-[#1E2C5A] hover:bg-[#141F45] text-xs uppercase tracking-[0.15em]"
                        data-testid={`pending-advance-${row.our_code}`}
                      >
                        {nextStage.key === "delivered" ? "Mark Delivered" : `→ ${nextStage.label}`}
                      </Button>
                    ) : (
                      <span className="text-xs text-green-700 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" strokeWidth={1.5} /> Done</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}


// ============ Numbering Master Panel ============
function NumberingMasterPanel() {
  const [masters, setMasters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newSize, setNewSize] = useState("");
  const [newPrefix, setNewPrefix] = useState("");
  const [newDefault, setNewDefault] = useState(1);
  const sortNumbering = useSortDir();

  const load = () => {
    setLoading(true);
    api.get("/admin/numbering-masters")
      .then(r => setMasters(r.data))
      .catch(() => setMasters([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const createMaster = async () => {
    const size = newSize.trim();
    const prefix = newPrefix.trim();
    if (!size || !prefix) { toast.error("Size and prefix are required"); return; }
    try {
      await api.post("/admin/numbering-masters", {
        size, prefix,
        default_starting: +newDefault || 1,
        sub_categories: [],
      });
      toast.success(`Numbering master created for ${size}`);
      setNewSize(""); setNewPrefix(""); setNewDefault(1); setShowAdd(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    }
  };

  const deleteMaster = async (m) => {
    if (!window.confirm(`Delete numbering master for size "${m.size}"? Existing product codes are NOT affected, but future products for this size will need manual codes.`)) return;
    try {
      await api.delete(`/admin/numbering-masters/${m.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="numbering-master-panel">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <p className="label-eyebrow">Central Code-Generation Engine</p>
          <h3 className="font-editorial text-2xl mt-1">Numbering Master</h3>
          <p className="text-xs text-[#8A8A8A] mt-2 max-w-2xl">
            One master per <strong>tile size</strong>. Each size has one immutable <strong>prefix</strong> and any number of sub-categories with their own starting series. New products auto-receive the next code — no more Excel sheets.
          </p>
        </div>
        {!showAdd && (
          <Button onClick={() => setShowAdd(true)} className="rounded-none bg-[#1E2C5A] hover:bg-[#141F45]" data-testid="new-master-btn">
            <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} /> New Size Master
          </Button>
        )}
      </div>

      {showAdd && (
        <div className="border border-[#C9A968]/40 bg-[#FDF9EE] p-6 mb-6" data-testid="new-master-form">
          <p className="label-eyebrow mb-4">Create a numbering master</p>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-4">
              <Label className="text-xs uppercase tracking-wider text-[#5C5C5C]">Tile Size</Label>
              <Input className="rounded-none" placeholder="e.g. 50x50 mm" value={newSize} onChange={(e) => setNewSize(e.target.value)} data-testid="new-master-size" />
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs uppercase tracking-wider text-[#5C5C5C]">Prefix (immutable)</Label>
              <Input className="rounded-none font-mono" placeholder="A" value={newPrefix} onChange={(e) => setNewPrefix(e.target.value)} data-testid="new-master-prefix" />
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs uppercase tracking-wider text-[#5C5C5C]">Default starting number</Label>
              <Input type="number" min="1" className="rounded-none" value={newDefault} onChange={(e) => setNewDefault(e.target.value)} data-testid="new-master-default" />
            </div>
            <div className="md:col-span-2 flex gap-2">
              <Button onClick={createMaster} className="rounded-none bg-[#1E2C5A] hover:bg-[#141F45] flex-1" data-testid="save-master">Save</Button>
              <button onClick={() => { setShowAdd(false); setNewSize(""); setNewPrefix(""); }} className="text-xs text-[#5C5C5C]">Cancel</button>
            </div>
          </div>
          <p className="text-xs text-[#8A8A8A] mt-3">You can add sub-categories (Glossy / Matt / Carving …) with their own starting series after creating the master.</p>
        </div>
      )}

      {loading ? (
        <div className="border border-dashed border-[#1A1A1A]/15 p-16 text-center text-[#8A8A8A]">Loading…</div>
      ) : masters.length === 0 ? (
        <div className="border border-dashed border-[#1A1A1A]/15 p-16 text-center text-[#8A8A8A]" data-testid="no-masters">
          No numbering masters yet. Add one for each tile size — e.g. size <strong>50x50 mm</strong>, prefix <strong>A</strong>.
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex justify-end">
            <SortToggle dir={sortNumbering.dir} onToggle={sortNumbering.toggle} testId="sort-numbering" />
          </div>
          {applySort(masters, sortNumbering.dir).map(m => (
            <MasterCard key={m.id} master={m} onChanged={load} onDelete={() => deleteMaster(m)} />
          ))}
        </div>
      )}
    </div>
  );
}

function MasterCard({ master, onChanged, onDelete }) {
  const [addingSub, setAddingSub] = useState(false);
  const [subName, setSubName] = useState("");
  const [subStart, setSubStart] = useState(1);
  const [editingId, setEditingId] = useState(null);
  const [editStart, setEditStart] = useState(0);
  const [editName, setEditName] = useState("");
  const sortSubsSeries = useSortDir();
  const sortSubsName = useSortDir();
  const [subsSortBy, setSubsSortBy] = useState("series"); // "series" | "name"

  const sortedSubs = useMemo(() => {
    const list = [...(master.sub_categories || [])];
    if (subsSortBy === "name") {
      list.sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
      if (sortSubsName.dir === "desc") list.reverse();
    } else {
      list.sort((a, b) => Number(a.starting_series || 0) - Number(b.starting_series || 0));
      if (sortSubsSeries.dir === "desc") list.reverse();
    }
    return list;
  }, [master.sub_categories, subsSortBy, sortSubsSeries.dir, sortSubsName.dir]);

  const addSub = async () => {
    if (!subName.trim()) { toast.error("Sub-category name is required"); return; }
    try {
      await api.post(`/admin/numbering-masters/${master.id}/subcategories`, {
        name: subName.trim(),
        starting_series: +subStart || 1,
      });
      toast.success("Sub-category added");
      setSubName(""); setSubStart(1); setAddingSub(false);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Add failed");
    }
  };

  const saveSub = async (subId) => {
    try {
      await api.patch(`/admin/numbering-masters/${master.id}/subcategories/${subId}`, {
        name: editName.trim(),
        starting_series: +editStart || 1,
      });
      toast.success("Updated");
      setEditingId(null);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const deleteSub = async (sub) => {
    if (!window.confirm(`Delete sub-category "${sub.name}"? Existing product codes are unaffected.`)) return;
    try {
      await api.delete(`/admin/numbering-masters/${master.id}/subcategories/${sub.id}`);
      toast.success("Sub-category removed");
      onChanged();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="border border-[#1A1A1A]/15 bg-white" data-testid={`master-card-${master.size}`}>
      <div className="flex items-center justify-between gap-4 p-5 border-b border-[#1A1A1A]/10 bg-[#F0EFEA]">
        <div className="flex items-center gap-6">
          <div>
            <p className="label-eyebrow">Size</p>
            <p className="font-editorial text-2xl mt-1">{master.size}</p>
          </div>
          <div>
            <p className="label-eyebrow">Prefix · locked</p>
            <p className="font-mono text-xl mt-1 px-3 py-1 bg-[#1E2C5A] text-white inline-block">{master.prefix}</p>
          </div>
          <div>
            <p className="label-eyebrow">Default next</p>
            <p className="font-mono text-lg mt-1">{master.prefix}{master.default_current || master.default_starting}</p>
          </div>
        </div>
        <button onClick={onDelete} className="text-red-700 hover:text-red-900 text-xs inline-flex items-center gap-1" data-testid={`del-master-${master.size}`}>
          <Trash2 className="w-3 h-3" strokeWidth={1.5} /> Delete size
        </button>
      </div>

      <div className="p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="label-eyebrow">Sub-categories · {(master.sub_categories || []).length}</p>
          {!addingSub && (
            <button onClick={() => setAddingSub(true)} className="text-xs uppercase tracking-[0.2em] text-[#1E2C5A] hover:text-[#141F45] inline-flex items-center gap-1" data-testid={`add-sub-${master.size}`}>
              <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> Add sub-category
            </button>
          )}
        </div>

        {addingSub && (
          <div className="grid grid-cols-12 gap-3 items-end p-4 bg-[#FAF9F6] border border-[#1A1A1A]/10 mb-4" data-testid="new-sub-form">
            <div className="col-span-5">
              <Label className="text-xs uppercase tracking-wider text-[#5C5C5C]">Name</Label>
              <Input className="rounded-none" placeholder="e.g. Glossy" value={subName} onChange={(e) => setSubName(e.target.value)} data-testid={`new-sub-name-${master.size}`} />
            </div>
            <div className="col-span-4">
              <Label className="text-xs uppercase tracking-wider text-[#5C5C5C]">Starting series</Label>
              <Input type="number" min="1" className="rounded-none font-mono" value={subStart} onChange={(e) => setSubStart(e.target.value)} data-testid={`new-sub-start-${master.size}`} />
            </div>
            <div className="col-span-3 flex gap-2">
              <Button onClick={addSub} className="rounded-none bg-[#1E2C5A] hover:bg-[#141F45] flex-1" data-testid={`save-sub-${master.size}`}>Save</Button>
              <button onClick={() => { setAddingSub(false); setSubName(""); }} className="text-xs text-[#5C5C5C]">Cancel</button>
            </div>
          </div>
        )}

        {(master.sub_categories || []).length === 0 ? (
          <p className="text-xs text-[#8A8A8A] py-4">No sub-categories. Products using this size will use the default sequence ({master.prefix}{master.default_current || master.default_starting}, {master.prefix}{(master.default_current || master.default_starting) + 1}…).</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">#</TableHead>
                <TableHead>
                  <div className="flex items-center gap-2">
                    <span>Sub-category</span>
                    <SortToggle
                      dir={sortSubsName.dir}
                      onToggle={() => { setSubsSortBy("name"); sortSubsName.toggle(); }}
                      testId={`sort-subs-name-${master.size}`}
                    />
                  </div>
                </TableHead>
                <TableHead>
                  <div className="flex items-center gap-2">
                    <span>Starting series</span>
                    <SortToggle
                      dir={sortSubsSeries.dir}
                      onToggle={() => { setSubsSortBy("series"); sortSubsSeries.toggle(); }}
                      testId={`sort-subs-series-${master.size}`}
                    />
                  </div>
                </TableHead>
                <TableHead>Current / next</TableHead>
                <TableHead className="w-32">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedSubs.map((s, i) => (
                <TableRow key={s.id} data-testid={`sub-row-${s.name}`}>
                  <TableCell className="text-xs text-[#8A8A8A]">{String(i + 1).padStart(2, "0")}</TableCell>
                  <TableCell>
                    {editingId === s.id ? (
                      <Input className="rounded-none h-8" value={editName} onChange={(e) => setEditName(e.target.value)} data-testid={`edit-sub-name-${s.name}`} />
                    ) : (
                      <span className="font-medium">{s.name}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingId === s.id ? (
                      <Input type="number" className="rounded-none h-8 w-32 font-mono" value={editStart} onChange={(e) => setEditStart(e.target.value)} data-testid={`edit-sub-start-${s.name}`} />
                    ) : (
                      <span className="font-mono">{master.prefix}{s.starting_series}</span>
                    )}
                  </TableCell>
                  <TableCell><span className="font-mono text-[#1E2C5A] font-medium">{master.prefix}{s.current_number}</span></TableCell>
                  <TableCell className="text-xs">
                    {editingId === s.id ? (
                      <div className="flex gap-2">
                        <button onClick={() => saveSub(s.id)} className="text-[#1E2C5A] hover:underline" data-testid={`save-edit-sub-${s.name}`}>Save</button>
                        <button onClick={() => setEditingId(null)} className="text-[#5C5C5C]">Cancel</button>
                      </div>
                    ) : (
                      <div className="flex gap-3">
                        <button onClick={() => { setEditingId(s.id); setEditName(s.name); setEditStart(s.starting_series); }} className="text-[#1E2C5A] hover:underline inline-flex items-center gap-1" data-testid={`edit-sub-${s.name}`}>
                          <Pencil className="w-3 h-3" strokeWidth={1.5} /> Edit
                        </button>
                        <button onClick={() => deleteSub(s)} className="text-red-700 hover:underline inline-flex items-center gap-1" data-testid={`del-sub-${s.name}`}>
                          <Trash2 className="w-3 h-3" strokeWidth={1.5} /> Delete
                        </button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}


// ============ Users Panel (Super admin only) ============
// Permission catalogue — kept in sync with the tab-visibility keys above.
const PERM_MODULES = [
  { key: "dashboard", label: "Dashboard" },
  { key: "products", label: "Products" },
  { key: "numbering", label: "Numbering" },
  { key: "tile-types", label: "Tile Types" },
  { key: "vendors", label: "Vendors" },
  { key: "buyers", label: "Buyers" },
  { key: "orders", label: "Orders" },
  { key: "check-stock", label: "Check Stock" },
  { key: "returns", label: "Returns" },
  { key: "completed", label: "Completed" },
  { key: "selected", label: "Selected Products" },
  { key: "referrals", label: "Referrals" },
  { key: "layers", label: "Tile Layers" },
  { key: "to-arrange", label: "To Arrange Goods" },
  { key: "pending-deliveries", label: "Pending Deliveries" },
  { key: "quotes", label: "Quotes" },
  { key: "appointments", label: "Appointments" },
  { key: "reports", label: "Reports" },
  { key: "payment-qr", label: "Payment QR" },
  { key: "payment-verify", label: "Payment Verification" },
];
const PERM_TIME_FILTERS = [
  { key: "filter.this_week", label: "This Week" },
  { key: "filter.this_month", label: "This Month" },
  { key: "filter.this_year", label: "This Year" },
  { key: "filter.past", label: "Past" },
];
const PERM_GLOBAL_ACTIONS = [
  { key: "action.export", label: "Export" },
  { key: "action.download", label: "Download" },
  { key: "action.print", label: "Print" },
];

function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRoleName, setNewRoleName] = useState("Sales");
  const [showPw, setShowPw] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editPerms, setEditPerms] = useState({});
  const [editRoleName, setEditRoleName] = useState("");
  const sortUsers = useSortDir();

  const load = () => {
    setLoading(true);
    api.get("/admin/users").then(r => setUsers(r.data)).catch(() => setUsers([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const createUser = async () => {
    if (!newEmail.trim() || !newEmail.includes("@")) { toast.error("Enter a valid email"); return; }
    if (newPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await api.post("/admin/users", {
        email: newEmail.trim().toLowerCase(),
        password: newPassword,
        role_name: newRoleName.trim() || "Staff",
        permissions: {},
        active: true,
      });
      toast.success("User created");
      setNewEmail(""); setNewPassword(""); setNewRoleName("Sales"); setShowAdd(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? They will lose access immediately.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const startEdit = (u) => {
    setEditingId(u.id);
    setEditPerms(u.permissions || {});
    setEditRoleName(u.role_name || "Staff");
  };
  const toggle = (key) => setEditPerms(p => ({ ...p, [key]: !p[key] }));

  const savePerms = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, {
        role_name: editRoleName.trim() || "Staff",
        permissions: editPerms,
      });
      toast.success(`Permissions saved for ${u.email}`);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const resetPassword = async (u) => {
    const np = window.prompt(`Set a new password for ${u.email} (min 6 chars):`);
    if (!np) return;
    if (np.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await api.patch(`/admin/users/${u.id}`, { password: np });
      toast.success("Password updated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { active: !u.active });
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  return (
    <div data-testid="users-panel">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <p className="label-eyebrow">Admin Access Control</p>
          <h3 className="font-editorial text-2xl mt-1">Users & Permissions</h3>
          <p className="text-xs text-[#8A8A8A] mt-2 max-w-2xl">
            Create staff accounts with an email + password. Tick the modules and actions each user is allowed to access. Un-ticked items are completely hidden from that user.
          </p>
        </div>
        {!showAdd && (
          <Button onClick={() => setShowAdd(true)} className="rounded-none bg-[#1E2C5A] hover:bg-[#141F45]" data-testid="new-user-btn">
            <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} /> Create User
          </Button>
        )}
      </div>

      {showAdd && (
        <div className="border border-[#C9A968]/40 bg-[#FDF9EE] p-6 mb-6" data-testid="new-user-form">
          <p className="label-eyebrow mb-4">New user</p>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-4">
              <Label className="text-xs uppercase tracking-wider text-[#5C5C5C]">Email (this is their login ID)</Label>
              <Input className="rounded-none" placeholder="sales@tileroom.in" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} data-testid="new-user-email" />
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs uppercase tracking-wider text-[#5C5C5C]">Password</Label>
              <Input className="rounded-none font-mono" placeholder="min 6 chars" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="new-user-password" />
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs uppercase tracking-wider text-[#5C5C5C]">Role name</Label>
              <Input className="rounded-none" placeholder="Sales / Accounts / Dispatch…" value={newRoleName} onChange={(e) => setNewRoleName(e.target.value)} data-testid="new-user-role" />
            </div>
            <div className="md:col-span-2 flex gap-2">
              <Button onClick={createUser} className="rounded-none bg-[#1E2C5A] hover:bg-[#141F45] flex-1" data-testid="save-user">Save</Button>
              <button onClick={() => { setShowAdd(false); setNewEmail(""); setNewPassword(""); }} className="text-xs text-[#5C5C5C]">Cancel</button>
            </div>
          </div>
          <p className="text-xs text-[#8A8A8A] mt-3">Tick permissions after creating — by default the user has no access to anything.</p>
        </div>
      )}

      {loading ? (
        <div className="border border-dashed border-[#1A1A1A]/15 p-16 text-center text-[#8A8A8A]">Loading…</div>
      ) : (
        <div className="space-y-6">
          <div className="flex justify-end">
            <SortToggle dir={sortUsers.dir} onToggle={sortUsers.toggle} testId="sort-users" />
          </div>
          {applySort(users, sortUsers.dir).map(u => (
            <div key={u.id} className="border border-[#1A1A1A]/15 bg-white" data-testid={`user-row-${u.email}`}>
              <div className="flex items-center justify-between p-5 border-b border-[#1A1A1A]/10 bg-[#F0EFEA] flex-wrap gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <p className="font-medium">{u.email}</p>
                    {u.is_super && <span className="text-[10px] uppercase tracking-wider bg-[#C9A968] text-white px-2 py-0.5">Super Admin</span>}
                    {!u.active && <span className="text-[10px] uppercase tracking-wider bg-red-100 text-red-800 px-2 py-0.5">Disabled</span>}
                  </div>
                  <p className="text-xs text-[#5C5C5C] mt-1">
                    Role: <span className="uppercase tracking-wider">{u.role_name}</span> ·
                    Password: <span className="font-mono ml-1">{showPw[u.id] ? u.password_plain : "•••••••"}</span>
                    <button onClick={() => setShowPw(s => ({ ...s, [u.id]: !s[u.id] }))} className="ml-2 text-[#1E2C5A] hover:underline text-[10px]" data-testid={`show-pw-${u.email}`}>
                      {showPw[u.id] ? "Hide" : "Show"}
                    </button>
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {!u.is_super && (
                    <>
                      <button onClick={() => resetPassword(u)} className="text-xs uppercase tracking-wider border border-[#1E2C5A] text-[#1E2C5A] px-3 py-1.5 hover:bg-[#1E2C5A] hover:text-white transition-colors" data-testid={`reset-pw-${u.email}`}>Reset Password</button>
                      <button onClick={() => toggleActive(u)} className="text-xs uppercase tracking-wider border border-[#8A8A8A] text-[#5C5C5C] px-3 py-1.5 hover:bg-[#8A8A8A] hover:text-white transition-colors" data-testid={`toggle-active-${u.email}`}>{u.active ? "Disable" : "Enable"}</button>
                      <button onClick={() => deleteUser(u)} className="text-xs uppercase tracking-wider border border-red-700 text-red-700 px-3 py-1.5 hover:bg-red-700 hover:text-white transition-colors" data-testid={`del-user-${u.email}`}><Trash2 className="w-3 h-3 inline mr-1" strokeWidth={1.5} />Delete</button>
                    </>
                  )}
                </div>
              </div>

              {u.is_super ? (
                <p className="p-5 text-xs text-[#8A8A8A]">Super admin has unrestricted access to everything — no permission grid to configure.</p>
              ) : (
                <div className="p-5">
                  <div className="flex items-end justify-between mb-4 gap-3 flex-wrap">
                    <div>
                      <p className="label-eyebrow">Permissions</p>
                      <p className="text-xs text-[#5C5C5C] mt-1">Tick anything this user is allowed to see or do.</p>
                    </div>
                    {editingId === u.id ? (
                      <div className="flex gap-2 items-end">
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Role name</Label>
                          <Input className="rounded-none h-8 w-40" value={editRoleName} onChange={(e) => setEditRoleName(e.target.value)} data-testid={`edit-role-${u.email}`} />
                        </div>
                        <Button onClick={() => savePerms(u)} className="rounded-none bg-[#1E2C5A] hover:bg-[#141F45] h-8" data-testid={`save-perms-${u.email}`}>Save</Button>
                        <button onClick={() => setEditingId(null)} className="text-xs text-[#5C5C5C]">Cancel</button>
                      </div>
                    ) : (
                      <button onClick={() => startEdit(u)} className="text-xs uppercase tracking-[0.2em] text-[#1E2C5A] hover:text-[#141F45] inline-flex items-center gap-1" data-testid={`edit-perms-${u.email}`}>
                        <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} /> Edit permissions
                      </button>
                    )}
                  </div>

                  <PermGrid
                    isEditing={editingId === u.id}
                    perms={editingId === u.id ? editPerms : (u.permissions || {})}
                    toggle={toggle}
                    testPrefix={u.email}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PermGrid({ isEditing, perms, toggle, testPrefix }) {
  const cb = (key, label) => (
    <label key={key} className={`flex items-center gap-2 text-xs py-1 ${isEditing ? "cursor-pointer" : ""}`}>
      <input
        type="checkbox"
        checked={!!perms[key]}
        disabled={!isEditing}
        onChange={() => isEditing && toggle(key)}
        className="accent-[#1E2C5A] w-3.5 h-3.5"
        data-testid={`perm-${testPrefix}-${key}`}
      />
      <span className={perms[key] ? "text-[#1A1A1A]" : "text-[#8A8A8A]"}>{label}</span>
    </label>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div>
        <p className="label-eyebrow mb-3">Modules (tab visibility)</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
          {PERM_MODULES.map(m => cb(`mod.${m.key}`, m.label))}
        </div>
      </div>
      <div>
        <p className="label-eyebrow mb-3">Time-range filters</p>
        <div className="space-y-1">{PERM_TIME_FILTERS.map(f => cb(f.key, f.label))}</div>
        <p className="label-eyebrow mt-6 mb-3">Global actions</p>
        <div className="space-y-1">{PERM_GLOBAL_ACTIONS.map(a => cb(a.key, a.label))}</div>
      </div>
      <div>
        <p className="label-eyebrow mb-3">Per-module actions</p>
        <p className="text-[10px] text-[#8A8A8A] mb-2">These sit on top of tab visibility. Tick to grant.</p>
        <div className="max-h-72 overflow-y-auto pr-2 space-y-1">
          {PERM_MODULES.flatMap(m => (
            ["view", "create", "edit", "delete", "approve"].map(act =>
              cb(`${m.key}.${act}`, `${m.label} · ${act.charAt(0).toUpperCase() + act.slice(1)}`)
            )
          ))}
        </div>
      </div>
    </div>
  );
}


// ============ Appointments Panel ============
const APPT_STATUS_OPTIONS = [
  { key: "pending",   label: "Pending" },
  { key: "confirmed", label: "Confirmed" },
  { key: "completed", label: "Completed" },
  { key: "cancelled", label: "Cancelled" },
];

function AppointmentsPanel() {
  const [appts, setAppts] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("upcoming");
  const [blockMode, setBlockMode] = useState("single"); // single | range
  const [newBlockDate, setNewBlockDate] = useState("");
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [skipSundays, setSkipSundays] = useState(true);
  const [newBlockReason, setNewBlockReason] = useState("");
  const [rescheduleAppt, setRescheduleAppt] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [rescheduleTime, setRescheduleTime] = useState("");
  const [rescheduleNote, setRescheduleNote] = useState("");
  const [rescheduleBusy, setRescheduleBusy] = useState(false);
  const sortAppts = useSortDir();

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/appointments").then(r => setAppts(r.data)),
      api.get("/admin/blocked-dates").then(r => setBlocked(r.data)),
    ]).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (a, next) => {
    try {
      await api.patch(`/admin/appointments/${a.id}`, { status: next });
      toast.success(`${a.customer_name}: ${next}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const deleteAppt = async (a) => {
    if (!window.confirm(`Delete appointment for ${a.customer_name} on ${a.date}?`)) return;
    try {
      await api.delete(`/admin/appointments/${a.id}`);
      toast.success("Appointment removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const openReschedule = (a) => {
    setRescheduleAppt(a);
    setRescheduleDate(a.date || "");
    setRescheduleTime(a.time_slot || "");
    setRescheduleNote("");
  };

  const closeReschedule = () => {
    setRescheduleAppt(null);
    setRescheduleDate("");
    setRescheduleTime("");
    setRescheduleNote("");
  };

  const submitReschedule = async () => {
    if (!rescheduleAppt) return;
    if (!rescheduleDate) { toast.error("Pick a new date"); return; }
    const today = new Date().toISOString().slice(0, 10);
    if (rescheduleDate < today) { toast.error("New date cannot be in the past"); return; }
    if (blocked.some(b => b.date === rescheduleDate)) {
      toast.error("That date is blocked. Unblock it first or pick another day.");
      return;
    }
    if (rescheduleDate === rescheduleAppt.date && (rescheduleTime || "") === (rescheduleAppt.time_slot || "")) {
      toast.error("Change the date or time slot before saving");
      return;
    }
    setRescheduleBusy(true);
    try {
      const patch = { date: rescheduleDate, time_slot: rescheduleTime || null };
      const prev = `${rescheduleAppt.date}${rescheduleAppt.time_slot ? " " + rescheduleAppt.time_slot : ""}`;
      const next = `${rescheduleDate}${rescheduleTime ? " " + rescheduleTime : ""}`;
      const adminNote = (rescheduleNote || "").trim();
      const stamp = `[Rescheduled ${new Date().toISOString().slice(0, 10)}] ${prev} → ${next}${adminNote ? " · " + adminNote : ""}`;
      patch.notes = rescheduleAppt.notes ? `${rescheduleAppt.notes}\n${stamp}` : stamp;
      await api.patch(`/admin/appointments/${rescheduleAppt.id}`, patch);
      toast.success(`Moved to ${next}. Please notify ${rescheduleAppt.customer_name} on WhatsApp.`);
      closeReschedule();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reschedule failed");
    } finally {
      setRescheduleBusy(false);
    }
  };

  const blockDate = async () => {
    if (!newBlockDate) { toast.error("Pick a date"); return; }
    try {
      await api.post("/admin/blocked-dates", { date: newBlockDate, reason: newBlockReason || null });
      toast.success(`Blocked ${newBlockDate}`);
      setNewBlockDate(""); setNewBlockReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Block failed");
    }
  };

  const blockRange = async () => {
    if (!rangeStart || !rangeEnd) { toast.error("Pick both start and end dates"); return; }
    try {
      const { data } = await api.post("/admin/blocked-dates/range", {
        start_date: rangeStart,
        end_date: rangeEnd,
        reason: newBlockReason || null,
        skip_sundays: skipSundays,
      });
      toast.success(`Added ${data.added} dates (${data.skipped_existing_or_sundays} skipped)`);
      setRangeStart(""); setRangeEnd(""); setNewBlockReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Block failed");
    }
  };

  const unblock = async (b) => {
    if (!window.confirm(`Un-block ${b.date}? Buyers will be able to book this date again.`)) return;
    try {
      await api.delete(`/admin/blocked-dates/${b.id}`);
      toast.success("Un-blocked");
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const visible = appts.filter(a => {
    if (filter === "upcoming") return a.date >= today && a.status !== "cancelled" && a.status !== "completed";
    if (filter === "past") return a.date < today || a.status === "completed";
    if (filter === "cancelled") return a.status === "cancelled";
    return true;
  });

  return (
    <div data-testid="appointments-panel">
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="label-eyebrow">Showroom · See Live Tile bookings</p>
          <h3 className="font-editorial text-2xl mt-1">Appointments</h3>
          <p className="text-xs text-[#8A8A8A] mt-2 max-w-2xl">
            Customers who tick <em>See Live Tile</em> at checkout book a date here. Block dates you can&apos;t receive visitors — those will be greyed out on the buyer&apos;s calendar.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select className="h-9 border border-input rounded-none px-2 text-xs bg-transparent" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="appt-filter">
            <option value="upcoming">Upcoming</option>
            <option value="past">Past / Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="all">All</option>
          </select>
          <SortToggle dir={sortAppts.dir} onToggle={sortAppts.toggle} testId="sort-appts" />
          <button onClick={load} className="text-xs uppercase tracking-[0.2em] text-[#1E2C5A] hover:text-[#141F45]" data-testid="appt-refresh">Refresh ↻</button>
        </div>
      </div>

      {/* Block a date / range */}
      <div className="border border-[#C9A968]/40 bg-[#FDF9EE] p-5 mb-6" data-testid="block-date-form">
        <div className="flex items-center gap-4 mb-3">
          <p className="label-eyebrow">Block dates from the buyer calendar</p>
          <div className="flex border border-[#C9A968]/40">
            <button
              onClick={() => setBlockMode("single")}
              className={`text-xs uppercase tracking-wider px-3 py-1 ${blockMode === "single" ? "bg-[#1E2C5A] text-white" : "text-[#5C5C5C]"}`}
              data-testid="block-mode-single"
            >Single</button>
            <button
              onClick={() => setBlockMode("range")}
              className={`text-xs uppercase tracking-wider px-3 py-1 ${blockMode === "range" ? "bg-[#1E2C5A] text-white" : "text-[#5C5C5C]"}`}
              data-testid="block-mode-range"
            >Range</button>
          </div>
        </div>

        {blockMode === "single" ? (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-3">
              <Label className="text-xs uppercase tracking-wider text-[#5C5C5C]">Date</Label>
              <Input type="date" className="rounded-none" value={newBlockDate} onChange={(e) => setNewBlockDate(e.target.value)} data-testid="block-date-input" />
            </div>
            <div className="md:col-span-6">
              <Label className="text-xs uppercase tracking-wider text-[#5C5C5C]">Reason (optional)</Label>
              <Input className="rounded-none" placeholder="e.g. Diwali, staff off-site" value={newBlockReason} onChange={(e) => setNewBlockReason(e.target.value)} data-testid="block-reason-input" />
            </div>
            <div className="md:col-span-3">
              <Button onClick={blockDate} className="rounded-none bg-[#1E2C5A] hover:bg-[#141F45] w-full" data-testid="block-date-btn">
                <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} /> Block This Date
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
              <div className="md:col-span-3">
                <Label className="text-xs uppercase tracking-wider text-[#5C5C5C]">Start date</Label>
                <Input type="date" className="rounded-none" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} data-testid="block-range-start" />
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs uppercase tracking-wider text-[#5C5C5C]">End date</Label>
                <Input type="date" className="rounded-none" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} data-testid="block-range-end" />
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs uppercase tracking-wider text-[#5C5C5C]">Reason (optional)</Label>
                <Input className="rounded-none" placeholder="e.g. Diwali holidays" value={newBlockReason} onChange={(e) => setNewBlockReason(e.target.value)} data-testid="block-range-reason" />
              </div>
              <div className="md:col-span-3">
                <Button onClick={blockRange} className="rounded-none bg-[#1E2C5A] hover:bg-[#141F45] w-full" data-testid="block-range-btn">
                  <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} /> Block Range
                </Button>
              </div>
            </div>
            <label className="flex items-center gap-2 mt-3 cursor-pointer">
              <input type="checkbox" checked={skipSundays} onChange={(e) => setSkipSundays(e.target.checked)} className="accent-[#1E2C5A] w-3.5 h-3.5" data-testid="block-skip-sundays" />
              <span className="text-xs text-[#5C5C5C]">Skip Sundays automatically</span>
            </label>
          </>
        )}
        {blocked.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="blocked-chips">
            {blocked.map(b => (
              <div key={b.id} className="flex items-center gap-2 bg-white border border-[#C9A968]/50 px-3 py-1 text-xs" data-testid={`blocked-${b.date}`}>
                <span className="font-medium">{b.date}</span>
                {b.reason && <span className="text-[#8A8A8A]">· {b.reason}</span>}
                <button onClick={() => unblock(b)} className="text-red-700 hover:underline ml-1">×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className="border border-dashed border-[#1A1A1A]/15 p-16 text-center text-[#8A8A8A]">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="border border-dashed border-[#1A1A1A]/15 p-16 text-center text-[#8A8A8A]" data-testid="no-appts">
          No appointments in this view.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>WhatsApp</TableHead>
              <TableHead>Notes</TableHead>
              <TableHead>Order</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-56">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {applySort(visible, sortAppts.dir).map(a => (
              <TableRow key={a.id} data-testid={`appt-row-${a.id}`}>
                <TableCell className="font-medium">{a.date}{a.time_slot ? <span className="text-[#8A8A8A] text-xs ml-2">{a.time_slot}</span> : null}</TableCell>
                <TableCell>{a.customer_name}</TableCell>
                <TableCell className="text-xs">{a.customer_whatsapp}</TableCell>
                <TableCell className="text-xs text-[#5C5C5C] max-w-xs">{a.notes || <span className="text-[#8A8A8A]">—</span>}</TableCell>
                <TableCell className="text-xs">{a.order_id ? <Link to={`/orders/${a.order_id}`} className="text-[#1E2C5A] hover:underline">Order</Link> : <span className="text-[#8A8A8A]">—</span>}</TableCell>
                <TableCell>
                  <select
                    className="h-8 border border-input rounded-none px-2 text-xs bg-transparent"
                    value={a.status}
                    onChange={(e) => updateStatus(a, e.target.value)}
                    data-testid={`appt-status-${a.id}`}
                  >
                    {APPT_STATUS_OPTIONS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => openReschedule(a)}
                      disabled={a.status === "cancelled" || a.status === "completed"}
                      className="text-[#1E2C5A] hover:underline text-xs inline-flex items-center gap-1 disabled:text-[#B8B8B8] disabled:no-underline disabled:cursor-not-allowed"
                      data-testid={`appt-reschedule-${a.id}`}
                      title={a.status === "cancelled" || a.status === "completed" ? "Not reschedulable in this state" : "Move to a different date/time"}
                    >
                      <Pencil className="w-3 h-3" strokeWidth={1.5} /> Reschedule
                    </button>
                    <button onClick={() => deleteAppt(a)} className="text-red-700 hover:underline text-xs inline-flex items-center gap-1" data-testid={`appt-del-${a.id}`}>
                      <Trash2 className="w-3 h-3" strokeWidth={1.5} /> Delete
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Reschedule dialog */}
      <Dialog open={!!rescheduleAppt} onOpenChange={(o) => { if (!o) closeReschedule(); }}>
        <DialogContent className="rounded-none max-w-lg" data-testid="reschedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-editorial text-2xl">Reschedule appointment</DialogTitle>
          </DialogHeader>
          {rescheduleAppt && (
            <div className="space-y-4 pt-2">
              <div className="border border-[#1A1A1A]/10 bg-[#FAFAFA] p-3 text-xs">
                <p className="text-[#5C5C5C]">
                  <span className="font-medium text-[#1A1A1A]">{rescheduleAppt.customer_name}</span>
                  <span className="mx-2 text-[#8A8A8A]">·</span>
                  {rescheduleAppt.customer_whatsapp}
                </p>
                <p className="mt-1 text-[#8A8A8A]">
                  Currently booked for <span className="text-[#1E2C5A] font-medium">{rescheduleAppt.date}</span>
                  {rescheduleAppt.time_slot ? <> at <span className="text-[#1E2C5A] font-medium">{rescheduleAppt.time_slot}</span></> : null}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5C5C5C]">New date</Label>
                  <Input
                    type="date"
                    className="rounded-none mt-1"
                    value={rescheduleDate}
                    min={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setRescheduleDate(e.target.value)}
                    data-testid="reschedule-date-input"
                  />
                  {rescheduleDate && blocked.some(b => b.date === rescheduleDate) && (
                    <p className="text-[11px] text-red-700 mt-1" data-testid="reschedule-blocked-warn">
                      This date is on your blocked list. Unblock it or pick another day.
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5C5C5C]">New time slot (optional)</Label>
                  <Input
                    className="rounded-none mt-1"
                    placeholder="e.g. 11:00 – 12:00"
                    value={rescheduleTime}
                    onChange={(e) => setRescheduleTime(e.target.value)}
                    data-testid="reschedule-time-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5C5C5C]">Reason / note (optional)</Label>
                <Input
                  className="rounded-none mt-1"
                  placeholder="e.g. Owner unavailable, requested by customer"
                  value={rescheduleNote}
                  onChange={(e) => setRescheduleNote(e.target.value)}
                  data-testid="reschedule-note-input"
                />
                <p className="text-[11px] text-[#8A8A8A] mt-1">A history line will be appended to the appointment&apos;s notes for audit.</p>
              </div>

              <div className="border-t border-[#1A1A1A]/10 pt-3 flex items-center justify-end gap-3">
                <button
                  onClick={closeReschedule}
                  className="text-xs uppercase tracking-[0.2em] text-[#5C5C5C] hover:text-[#1A1A1A]"
                  data-testid="reschedule-cancel"
                >
                  Cancel
                </button>
                <Button
                  onClick={submitReschedule}
                  disabled={rescheduleBusy}
                  className="rounded-none bg-[#1E2C5A] hover:bg-[#141F45]"
                  data-testid="reschedule-save"
                >
                  {rescheduleBusy ? "Saving…" : "Confirm Reschedule"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ============ Reports Panel (Daily Business Report) ============
function ReportsPanel({ me }) {
  const [cfg, setCfg] = useState(null);
  const [recipientsText, setRecipientsText] = useState("");
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testBusy, setTestBusy] = useState(false);

  const loadCfg = () =>
    api.get("/admin/reports/config").then(r => {
      setCfg(r.data);
      setRecipientsText((r.data.recipients || []).join(", "));
    });
  const loadHistory = () =>
    api.get("/admin/reports/history").then(r => setHistory(r.data)).catch(() => setHistory([]));
  useEffect(() => { loadCfg(); loadHistory(); }, []);

  const saveCfg = async (partial) => {
    if (!me.is_super) { toast.error("Only super admin can change reports config"); return; }
    setBusy(true);
    try {
      const recipients = recipientsText.split(",").map(s => s.trim()).filter(Boolean);
      const body = { recipients, ...partial };
      const { data } = await api.patch("/admin/reports/config", body);
      setCfg(data);
      toast.success("Report settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const generateNow = async () => {
    const tok = localStorage.getItem("dtile_admin_token");
    toast.info("Generating today's PDF…");
    try {
      const resp = await fetch(`${API}/admin/reports/generate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `TileRoom_Daily_Report_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) {
      toast.error("Preview failed");
    }
  };

  const sendNow = async () => {
    if (!me.is_super) { toast.error("Only super admin can send reports"); return; }
    try {
      const { data } = await api.post("/admin/reports/send-now");
      if (data.ok) toast.success(`Emailed to ${(data.recipients || []).length} recipient(s)`);
      else toast.error(`Not sent: ${data.detail}`);
      loadHistory(); loadCfg();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
  };

  const sendTest = async () => {
    const email = (testEmail || "").trim();
    if (!email || !email.includes("@")) { toast.error("Enter a valid email address"); return; }
    setTestBusy(true);
    try {
      const { data } = await api.post("/admin/reports/send-test", { email });
      if (data.ok) toast.success(`Test report sent to ${data.recipient}`);
      else toast.error(`Not sent: ${data.detail}`);
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test send failed");
    } finally { setTestBusy(false); }
  };

  const downloadRun = async (runId, date) => {
    const tok = localStorage.getItem("dtile_admin_token");
    try {
      const resp = await fetch(`${API}/admin/reports/${runId}/download`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `TileRoom_Report_${(date || "").slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  if (!cfg) return <div className="p-16 text-center text-[#8A8A8A]">Loading…</div>;

  return (
    <div data-testid="reports-panel">
      <div className="mb-6">
        <p className="label-eyebrow">Automation</p>
        <h3 className="font-editorial text-2xl mt-1">Daily Business Report</h3>
        <p className="text-xs text-[#8A8A8A] mt-2 max-w-2xl">
          A branded PDF summary is emailed automatically at your chosen time every day. Covers sales, backorders, inventory, new customers, appointments and KPIs.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 border border-[#1A1A1A]/15 bg-white p-6 space-y-4" data-testid="reports-config">
          <div>
            <Label className="label-eyebrow">Recipient emails (comma-separated)</Label>
            <Input
              className="rounded-none mt-1"
              placeholder="owner@tileroom.in, accounts@tileroom.in"
              value={recipientsText}
              onChange={(e) => setRecipientsText(e.target.value)}
              disabled={!me.is_super}
              data-testid="reports-recipients"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="label-eyebrow">Send time (24h, IST)</Label>
              <Input
                type="time"
                className="rounded-none mt-1"
                value={cfg.send_time}
                onChange={(e) => setCfg({ ...cfg, send_time: e.target.value })}
                disabled={!me.is_super}
                data-testid="reports-send-time"
              />
            </div>
            <div>
              <Label className="label-eyebrow">Frequency</Label>
              <select
                className="w-full h-10 border border-input rounded-none px-2 text-sm bg-transparent mt-1"
                value={cfg.frequency}
                onChange={(e) => setCfg({ ...cfg, frequency: e.target.value })}
                disabled={!me.is_super}
                data-testid="reports-frequency"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly (Mondays)</option>
                <option value="monthly">Monthly (1st)</option>
              </select>
            </div>
            <div>
              <Label className="label-eyebrow">Automation</Label>
              <label className="flex items-center gap-2 mt-3 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={cfg.active}
                  onChange={(e) => setCfg({ ...cfg, active: e.target.checked })}
                  disabled={!me.is_super}
                  className="accent-[#1E2C5A] w-4 h-4"
                  data-testid="reports-active"
                />
                <span className={cfg.active ? "text-green-800 font-medium" : "text-[#8A8A8A]"}>
                  {cfg.active ? "Active — will auto-send" : "Paused"}
                </span>
              </label>
            </div>
          </div>
          <div className="flex gap-3 flex-wrap pt-2">
            <Button
              onClick={() => saveCfg({ send_time: cfg.send_time, frequency: cfg.frequency, active: cfg.active })}
              disabled={busy || !me.is_super}
              className="rounded-none bg-[#1E2C5A] hover:bg-[#141F45]"
              data-testid="reports-save"
            >
              {busy ? "Saving…" : "Save Settings"}
            </Button>
            <Button onClick={generateNow} className="rounded-none bg-transparent border border-[#1E2C5A] text-[#1E2C5A] hover:bg-[#1E2C5A] hover:text-white" data-testid="reports-generate">
              <Download className="w-4 h-4 mr-1" strokeWidth={1.5} /> Preview PDF Now
            </Button>
            <Button
              onClick={sendNow}
              disabled={!me.is_super}
              className="rounded-none bg-[#C9A968] hover:bg-[#B69457] text-[#1E2C5A]"
              data-testid="reports-send-now"
            >
              Send Report Now
            </Button>
          </div>
          {cfg.last_status && (
            <p className="text-xs text-[#8A8A8A] pt-2 border-t border-[#1A1A1A]/10" data-testid="reports-last-status">
              Last run: {cfg.last_sent_at ? new Date(cfg.last_sent_at).toLocaleString() : "—"} · {cfg.last_status}
            </p>
          )}
        </div>

        <div className="border border-[#C9A968]/40 bg-[#FDF9EE] p-6 text-xs text-[#8B7A3E] space-y-4">
          <div className="space-y-3">
            <p className="label-eyebrow !text-[#8B7A3E]">Send a test email</p>
            <p className="text-[11px] leading-relaxed">Fires the current report as a one-off to any email — production recipients above will <strong>not</strong> be notified. Useful for verifying SMTP setup.</p>
            <Input
              type="email"
              className="rounded-none bg-white h-9 text-[#1A1A1A]"
              placeholder="you@yourdomain.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              data-testid="reports-test-email-input"
            />
            <Button
              onClick={sendTest}
              disabled={testBusy || !testEmail}
              className="w-full rounded-none bg-[#1E2C5A] hover:bg-[#141F45] text-white h-9 text-xs uppercase tracking-[0.2em]"
              data-testid="reports-send-test"
            >
              {testBusy ? "Sending…" : "Send Test Email"}
            </Button>
          </div>
          <div className="pt-3 border-t border-[#C9A968]/40 space-y-2">
            <p className="label-eyebrow !text-[#8B7A3E]">SMTP setup</p>
            <p>Reports are emailed via Gmail SMTP. Requires:</p>
            <ul className="list-disc list-inside space-y-1">
              <li><span className="font-mono">SMTP_USER</span> — your Gmail address</li>
              <li><span className="font-mono">SMTP_PASS</span> — a 16-char Google <em>App Password</em> (from <span className="font-mono">myaccount.google.com/apppasswords</span>)</li>
            </ul>
            <p>Preview PDF works immediately without SMTP.</p>
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="label-eyebrow">Report history · last 50</p>
          <button onClick={loadHistory} className="text-xs uppercase tracking-[0.2em] text-[#1E2C5A] hover:text-[#141F45]" data-testid="reports-refresh-history">Refresh ↻</button>
        </div>
        {history.length === 0 ? (
          <div className="border border-dashed border-[#1A1A1A]/15 p-12 text-center text-[#8A8A8A]" data-testid="reports-no-history">No reports run yet.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Generated</TableHead>
                <TableHead>Sent</TableHead>
                <TableHead>Recipients</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Size</TableHead>
                <TableHead className="w-24"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.map(r => (
                <TableRow key={r.id} data-testid={`report-run-${r.id}`}>
                  <TableCell className="text-xs">{new Date(r.generated_at).toLocaleString()}</TableCell>
                  <TableCell className="text-xs">{r.sent_at ? new Date(r.sent_at).toLocaleString() : <span className="text-[#8A8A8A]">—</span>}</TableCell>
                  <TableCell className="text-xs">{(r.recipients || []).join(", ") || <span className="text-[#8A8A8A]">—</span>}</TableCell>
                  <TableCell className="text-xs">
                    <span className={r.status === "sent" ? "text-green-700 font-medium" : r.status === "failed" ? "text-red-700" : "text-[#5C5C5C]"}>
                      {r.status}
                    </span>
                    {r.detail && <span className="text-[10px] text-[#8A8A8A] ml-1">· {r.detail}</span>}
                  </TableCell>
                  <TableCell className="text-xs text-[#8A8A8A]">{Math.round((r.pdf_size || 0) / 1024)} KB</TableCell>
                  <TableCell>
                    <button onClick={() => downloadRun(r.id, r.generated_at)} className="text-xs text-[#1E2C5A] hover:underline inline-flex items-center gap-1" data-testid={`download-report-${r.id}`}>
                      <Download className="w-3 h-3" strokeWidth={1.5} /> PDF
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

